/**
 * @fileoverview PDF viewer component using pdf.js with lazy loading.
 * @module ui/PdfViewer
 */

import { PdfUrlDialog } from './PdfUrlDialog.js';
import { PdfLeadGoToDialog } from './PdfLeadGoToDialog.js';
import { PdfHighlightStore } from './PdfHighlightStore.js';
import { PdfHighlightsListDialog } from './PdfHighlightsListDialog.js';
import { TEXT_CONSTANTS, MAP_CONSTANTS, LEADS_SECTION_LABELS } from '../constants.js';
import { setupTextSearchInput } from '../utils/InputHandlerFactory.js';
import { getDefaultLeadIdPatterner } from '../utils/LeadIdPatterner.js';
import { showStatusWarning, escapeHtml, urlExtension, urlFileName } from '../utils/jrfuncs.js';
import { getStaticBasePathOrDefault } from '../config/pageConfig.js';


// pdf.js is self-hosted, the one copy the bookshelf viewer also uses
// (code/core/static/libs/pdfjs/<version>/ -> /static/libs/pdfjs/<version>/). Same-origin
// serving also lets pdf.js start a REAL module worker (a cross-origin workerSrc silently
// fell back to the main-thread "fake worker"). The runtime-computed path keeps esbuild from
// resolving the import() into main.bundle.js, so the library stays lazy-loaded.
// The folder is named after the pdf.js version, so an upgrade (a new folder) changes these
// urls and no ?v= cache buster is needed against nginx's 30-day /static/ cache; the module
// is the same url the bookshelf viewer's own pages import, so the two share one cached copy.
// ATTN COUPLING: an upgrade changes PDFJS_STATIC_DIR here (then `npm run build:map`) and the
// two templates naming the viewer (docs/reference/self-hosted-packages.md); re-test this map viewer.
const PDFJS_STATIC_DIR = 'libs/pdfjs/6.4.299/';
// The static base comes from the page's #mapAppConfig element (config/pageConfig.js); it is read
// when the library is first loaded, not at import time, since modules evaluate before the app runs.
const pdfjsScriptUrl = () => `${getStaticBasePathOrDefault()}${PDFJS_STATIC_DIR}build/pdf.mjs`;
const pdfjsWorkerUrl = () => `${getStaticBasePathOrDefault()}${PDFJS_STATIC_DIR}build/pdf.worker.mjs`;
// The character maps and standard font data that ship beside the viewer (web/cmaps/, web/standard_fonts/; the bookshelf
// viewer finds them on its own): pdf.js needs them for a pdf whose fonts use a predefined CJK encoding or are not
// embedded, and refuses such a pdf without them (Oct 2026, Japanese and Chinese casebooks).
const pdfjsDocumentOptions = () => ({
    cMapUrl: `${getStaticBasePathOrDefault()}${PDFJS_STATIC_DIR}web/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `${getStaticBasePathOrDefault()}${PDFJS_STATIC_DIR}web/standard_fonts/`,
});

// Limits for the page highlight-indicator tooltip, which lists the page's notes.
// A native title tooltip has no scrollbar, so an unbounded list would run off screen.
const PDF_INDICATOR_MAX_TOOLTIP_NOTES = 10;
const PDF_INDICATOR_MAX_TOOLTIP_NOTE_CHARS = 120;

// Touch selection support. A touch device has no right-click and usually no
// keyboard, so a finished selection pops the action menu by itself.
// Dragging a selection handle fires selectionchange continuously; the menu is
// only shown once the user has stopped moving for this long, otherwise it would
// flicker under the handles being dragged.
const PDF_TOUCH_SELECTION_MENU_DELAY_MS = 450;
// Resolving a selection into anchors walks every text span on the page, and
// selectionchange fires on every mouse-move of a drag — so the capture waits for
// the selection to settle too (shorter, since it also drives the button state).
const PDF_SELECTION_CAPTURE_DELAY_MS = 200;
// Gap between the selection (or tapped highlight) and the menu anchored to it.
const PDF_TOUCH_MENU_OFFSET_PX = 10;
// Movement below this counts as a tap rather than a drag.
const PDF_TOUCH_TAP_SLOP_PX = 12;
// Presses longer than this are a long-press (OS selection gesture), not a tap.
const PDF_TOUCH_TAP_MAX_MS = 600;
// How long the dismiss handler ignores a click after a touch menu opens.
const PDF_TOUCH_MENU_DISMISS_GRACE_MS = 700;

// Swipe-to-turn-the-page. Horizontal distance a flick must cover to count,
// and how much more horizontal than vertical it has to be (so that flicking
// the page up and down never turns it by accident).
const PDF_SWIPE_MIN_DISTANCE_PX = 60;
const PDF_SWIPE_HORIZONTAL_RATIO = 1.8;
// An upper bound on duration keeps a slow drag — a pan, or a selection being
// adjusted — from registering as a flick.
const PDF_SWIPE_MAX_MS = 800;
// A swipe that starts on top of a pdf hyperlink must not also follow it on lift.
const PDF_SWIPE_CLICK_SUPPRESS_MS = 500;
// Slack when deciding whether the page is scrolled to its horizontal edge.
const PDF_SWIPE_SCROLL_EDGE_SLOP_PX = 2;

// Double-tap-to-turn-the-page. A double tap on the right third of the page area
// reads forward, on the left third backward; the middle third is left alone so
// tapping around a selection or a highlight can never turn the page by surprise.
// Max ms is measured lift-to-lift; the slop is how far apart the two taps may land.
const PDF_DOUBLE_TAP_MAX_MS = 400;
const PDF_DOUBLE_TAP_SLOP_PX = 40;
const PDF_DOUBLE_TAP_ZONE_FRACTION = 1 / 3;

// Arrow Up / Down scroll the page area this far: the step of a browser's own arrow-key scroll.
const PDF_ARROW_KEY_SCROLL_PX = 40;
// The reader keys that still work while the toolbar's page-number or search box has the focus.
// One-line fields have no use for them, and the focus stays in the box after Enter, so typing a
// page number and then pressing PageDown used to do nothing.
const PDF_KEYS_FROM_TOOLBAR_FIELDS = new Set(['PageUp', 'PageDown', 'F2', 'F3']);


/**
 * PDF viewer component that displays PDF files using pdf.js.
 * Lazy-loads the pdf.js library only when first needed.
 * @class
 */
export class PdfViewer {
    /**
     * Creates a new PdfViewer instance.
     * @param {HTMLElement} container - The container element to render the PDF viewer in
     * @param {Object} [options={}] - Configuration options
     * @param {Object} [options.stateManager=null] - StateManager instance for persisting URL history
     * @param {Function} [options.onBookmarkClick=null] - Callback invoked when a bookmark/link is clicked: onBookmarkClick(id, label, pageNumber)
     * @param {Function} [options.shouldLinkPdfBookmark=null] - Callback to determine if a bookmark title should be rendered as a hyperlink: shouldLinkPdfBookmark(titleLabel) => boolean
     * @param {Function} [options.onCopyToPlayLog=null] - Callback invoked to copy selected text to the play log: onCopyToPlayLog(text, pageNum, bookmarkLabel, prefix). `prefix` is a short tag ("Copy" or "Highlight") indicating the action that produced the log entry.
     * @param {Function} [options.onLeadGoToAttempt=null] - Callback invoked after the user submits the Ctrl+G "go to lead" dialog: onLeadGoToAttempt(leadInput, flagFound)
     * @param {Function} [options.onPlaylistMedia=null] - Callback invoked when a link to an .m3u playlist is clicked: onPlaylistMedia(url). Adds/selects/plays it in the Background Music tab.
     * @param {Function} [options.onTrackMedia=null] - Callback invoked when a link to an audio file is clicked: onTrackMedia(url, {loop}). Adds it to the User Playlist and plays it in the Background Music tab; loop is true for legacy ?backgroundLoop links.
     */
    constructor(container, options = {}) {
        this.container = container;
        this.stateManager = options.stateManager || null;
        this.onBookmarkClick = options.onBookmarkClick || null;
        this.shouldLinkPdfBookmark = options.shouldLinkPdfBookmark || null;
        this.onCopyToPlayLog = options.onCopyToPlayLog || null;
        this.onLeadGoToAttempt = options.onLeadGoToAttempt || null;
        this.onPlaylistMedia = options.onPlaylistMedia || null;
        this.onTrackMedia = options.onTrackMedia || null;
        this.pdfDoc = null;
        this.currentPage = 1;
        this.totalPages = 0;
        this.scale = 1.0;
        this.pdfjsLib = null;
        this.isLibraryLoaded = false;
        this.isLoading = false;
        this.currentUrl = null;
        this.currentFileName = null;
        // renderPage bookkeeping (see renderPage): the pdf.js task drawing the canvas, a counter that
        // each call bumps so an overtaken call stops, and the page the newest call is drawing (null
        // when none is pending), which page turns and refits start from instead of the page on screen
        this.renderTask = null;
        this.renderGeneration = 0;
        this.renderTargetPage = null;

        this.viewerEl = null;
        this.canvasEl = null;
        this.textLayerEl = null;
        this.controlsEl = null;
        this.pageInfoEl = null;
        this.bookmarkSidebarEl = null;
        this.isBookmarkSidebarOpen = false;

        this.resizeObserver = null;
        this.resizeTimerId = null;
        this.lastZoomMode = null; // 'fitPage', 'fitWidth', or 'manual'

        // Bookmark cache (populated by populateBookmarkSidebar). The outline is read from pdf.js once
        // per document (getCachedBookmarkData): bookmarkDataDoc is the document the pending/finished
        // read belongs to, so a new document always gets a fresh read.
        this.bookmarkCache = [];
        this.bookmarkDataDoc = null;
        this.bookmarkDataPromise = null;

        // Search state
        this.searchQuery = '';
        this.searchResults = [];         // Array of {pageNum, matchIndex, text}
        this.currentMatchIndex = -1;     // Currently highlighted match
        this.pageTextCache = new Map();  // Cache: pageNum -> extracted text items
        // bumped by every search and every clearSearch, so a search overtaken while it awaits page text
        // stops (see performSearch), like renderGeneration does for renderPage
        this.searchGeneration = 0;
        this.searchInputEl = null;
        this.searchClearBtnEl = null;
        this.matchInfoEl = null;
        this.searchCleanup = null;       // Cleanup function for input handler

        // Persistent text-highlight state
        this.highlightStore = new PdfHighlightStore();
        this.pageHighlights = new Map();  // pageNum -> [{id, pageNum, startItemIdx, startOffset, endItemIdx, endOffset, text, createdAt}]
        this.highlightsByDocUrl = null;   // URL key used for persisting current doc's highlights (null = don't persist)
        // Cursor for F2 / Shift+F2 highlight-to-highlight navigation. Holds the id
        // rather than an index because the ordered list is rebuilt on each step and
        // can change under us (highlights added/removed between presses).
        this.currentHighlightNavId = null;
        // Both top corners carry the same dot: when zoomed in, only part of the page
        // is on screen, so one corner alone is easy to miss.
        this.pageHighlightIndicatorEls = [];

        // Last usable text selection inside the text layer: {anchors, text, pdfY, pdfX},
        // captured on every selectionchange. Button-driven highlighting reads THIS rather
        // than window.getSelection(), because pressing a button collapses the live
        // selection before the click handler runs (and on touch the OS drops the
        // selection for reasons of its own). Cleared on page render and after a
        // highlight is applied, so a stale capture can never be re-applied.
        this.cachedSelection = null;
        // Debounce timers driven by selectionchange (see onSelectionChanged).
        this.selectionCaptureTimerId = null;
        this.selectionMenuTimerId = null;
        // Pointer type of the last press inside the text layer. Decides whether a
        // finished selection should auto-pop the action menu (touch has no right-click).
        this.lastPointerWasTouch = false;
        // Start position of the current text-layer press, for tap-vs-drag detection.
        this.pointerDownPos = null;
        // Deadline (epoch ms) during which the document-level dismiss handler ignores
        // one click, so the very tap that opened a touch menu doesn't close it again.
        this.suppressDismissUntil = 0;
        // In-progress touch gesture over the page area: {x, y, t, scrollLeft, target}.
        // swipeBlocked marks a gesture that turned into a pinch and must not turn a page.
        this.swipeStart = null;
        this.swipeBlocked = false;
        // Previous completed tap over the page area ({x, y, t}), for double-tap detection.
        this.lastTouchTap = null;
        // Deadline during which a click over the page area is swallowed, so the lift
        // of a page-turning swipe doesn't also follow a pdf hyperlink it passed over.
        this.suppressClickUntil = 0;
        // Live reference to the context menu's click handler, so a menu that is
        // re-shown without being clicked cannot leave a stale listener behind.
        this.contextMenuClickHandler = null;

        // PDF width of the currently rendered page, in PDF user units. Used
        // for the multi-column layout detector below.
        this.currentPagePdfWidth = null;
        // Multi-column layout info for the currently rendered page, derived
        // from the page's actual text item X distribution (not from bookmark
        // presence, which could miss columns that contain no bookmarks):
        //   { multiColumn: boolean, columnBoundary: number|null }
        // Used by findNearestPriorBookmarkLabel for column-aware matching.
        this.currentPageColumnInfo = null;

        this.initializeUI();
    }


    /**
     * Initializes the PDF viewer UI elements.
     * @private
     */
    initializeUI() {
        this.container.innerHTML = '';

        // Create viewer wrapper
        this.viewerEl = document.createElement('div');
        this.viewerEl.className = 'pdf-viewer';
        this.viewerEl.tabIndex = 0; // Make focusable for keyboard events

        // Create toolbar/controls (includes load button on right side)
        this.controlsEl = document.createElement('div');
        this.controlsEl.className = 'pdf-viewer-controls';
        this.controlsEl.innerHTML = `
            <button class="pdf-btn pdf-sidebar-toggle-btn" title="Toggle bookmarks">
                <i class="bi bi-list-nested"></i>
            </button>
            <span class="pdf-separator"></span>
            <button class="pdf-btn pdf-prev-btn" title="Previous page">
                <i class="bi bi-chevron-left"></i>
            </button>
            <span class="pdf-page-info">
                <input type="number" class="pdf-page-input" min="1" value="1"> / <span class="pdf-total-pages">0</span>
            </span>
            <button class="pdf-btn pdf-next-btn" title="Next page">
                <i class="bi bi-chevron-right"></i>
            </button>
            <span class="pdf-separator"></span>
            <button class="pdf-btn pdf-zoom-out-btn" title="Zoom out">
                <i class="bi bi-zoom-out"></i>
            </button>
            <span class="pdf-zoom-info">100%</span>
            <button class="pdf-btn pdf-zoom-in-btn" title="Zoom in">
                <i class="bi bi-zoom-in"></i>
            </button>
            <button class="pdf-btn pdf-fit-width-btn" title="Fit to width">
                <i class="bi bi-arrows-expand"></i>
            </button>
            <button class="pdf-btn pdf-fit-page-btn" title="Fit entire page">
                <i class="bi bi-aspect-ratio"></i>
            </button>
            <span class="pdf-separator"></span>
            <span class="pdf-search-group">
                <i class="bi bi-search pdf-search-icon"></i>
                <input type="text" class="pdf-search-input" placeholder="Search..." title="Search in PDF (Ctrl+F)">
                <button class="pdf-search-clear" title="Clear search">
                    <i class="bi bi-x"></i>
                </button>
            </span>
            <button class="pdf-btn pdf-search-prev-btn" title="Previous match (Shift+F3) — with an empty search box, previous highlight">
                <i class="bi bi-chevron-up"></i>
            </button>
            <button class="pdf-btn pdf-search-next-btn" title="Next match (F3) — with an empty search box, next highlight">
                <i class="bi bi-chevron-down"></i>
            </button>
            <button class="pdf-btn pdf-highlight-selection-btn" title="Highlight the selected text (Ctrl+Y)">
                <i class="bi bi-highlighter"></i>
            </button>
            <button class="pdf-btn pdf-highlights-list-btn" title="Show highlights and notes (F2 / Shift+F2 step through them)">
                <i class="bi bi-card-checklist"></i>
            </button>
            <span class="pdf-match-info"></span>
            <span class="pdf-controls-spacer"></span>
            <span class="pdf-current-url"></span>
            <button class="pdf-load-pdf-btn" title="Load a PDF from URL or local file">
                <i class="bi bi-file-earmark-pdf"></i> <span class="pdf-load-pdf-text">Load Case PDF...</span>
            </button>
        `;

        // Create bookmark sidebar
        this.bookmarkSidebarEl = document.createElement('div');
        this.bookmarkSidebarEl.className = 'pdf-bookmark-sidebar';
        this.bookmarkSidebarEl.innerHTML = `
            <div class="pdf-bookmark-header">
                <span>Bookmarks</span>
            </div>
            <div class="pdf-bookmark-list"></div>
        `;

        // Create main content wrapper (sidebar + canvas area)
        const mainContentEl = document.createElement('div');
        mainContentEl.className = 'pdf-main-content';
        this.mainContentEl = mainContentEl;

        // Create canvas container (scrollable)
        const canvasContainer = document.createElement('div');
        canvasContainer.className = 'pdf-canvas-container';

        // Create canvas wrapper (positions canvas and annotation layer together)
        this.canvasWrapperEl = document.createElement('div');
        this.canvasWrapperEl.className = 'pdf-canvas-wrapper';

        // Create canvas for rendering (start with 0 size so it doesn't cause scrollbars)
        this.canvasEl = document.createElement('canvas');
        this.canvasEl.className = 'pdf-canvas';
        this.canvasEl.width = 0;
        this.canvasEl.height = 0;
        this.canvasWrapperEl.appendChild(this.canvasEl);

        // Create text layer for native text selection
        this.textLayerEl = document.createElement('div');
        this.textLayerEl.className = 'pdf-text-layer';
        this.canvasWrapperEl.appendChild(this.textLayerEl);

        // Create annotation layer for clickable PDF links
        this.annotationLayerEl = document.createElement('div');
        this.annotationLayerEl.className = 'pdf-annotation-layer';
        this.canvasWrapperEl.appendChild(this.annotationLayerEl);

        // Corner dots marking "this page has highlights and/or notes", one in each top
        // corner. They live in the canvas wrapper (which is sized to the page), so they
        // track the page corners at any zoom. Hidden until updatePageHighlightIndicator
        // says otherwise.
        this.pageHighlightIndicatorEls = ['left', 'right'].map((side) => {
            const dot = document.createElement('div');
            dot.className = `pdf-page-highlight-indicator pdf-page-highlight-indicator-${side}`;
            dot.style.display = 'none';
            this.canvasWrapperEl.appendChild(dot);
            return dot;
        });

        canvasContainer.appendChild(this.canvasWrapperEl);

        // Create placeholder message (shown when no PDF is loaded)
        this.placeholderEl = document.createElement('div');
        this.placeholderEl.className = 'pdf-placeholder';
        this.placeholderEl.innerHTML = TEXT_CONSTANTS.PDF_NO_FILE_LOADED;
        canvasContainer.appendChild(this.placeholderEl);

        // Assemble main content (sidebar + canvas)
        mainContentEl.appendChild(this.bookmarkSidebarEl);
        mainContentEl.appendChild(canvasContainer);

        // Create loading overlay
        this.loadingEl = document.createElement('div');
        this.loadingEl.className = 'pdf-loading';
        this.loadingEl.innerHTML = '<div class="pdf-loading-spinner"></div><span>Loading PDF...</span>';
        this.loadingEl.style.display = 'none';

        // Create error display
        this.errorEl = document.createElement('div');
        this.errorEl.className = 'pdf-error';
        this.errorEl.style.display = 'none';

        // Create warning toast (for flash messages)
        this.warningToastEl = document.createElement('div');
        this.warningToastEl.className = 'pdf-warning-toast';

        // Create custom context menu for text selection
        this.contextMenuEl = document.createElement('div');
        this.contextMenuEl.className = 'pdf-context-menu';
        this.contextMenuEl.style.display = 'none';

        // Assemble viewer
        this.viewerEl.appendChild(this.controlsEl);
        this.viewerEl.appendChild(mainContentEl);
        this.viewerEl.appendChild(this.loadingEl);
        this.viewerEl.appendChild(this.errorEl);
        this.viewerEl.appendChild(this.warningToastEl);
        this.viewerEl.appendChild(this.contextMenuEl);
        this.container.appendChild(this.viewerEl);

        // Cache control elements
        this.currentUrlEl = this.controlsEl.querySelector('.pdf-current-url');
        this.pageInputEl = this.controlsEl.querySelector('.pdf-page-input');
        this.totalPagesEl = this.controlsEl.querySelector('.pdf-total-pages');
        this.zoomInfoEl = this.controlsEl.querySelector('.pdf-zoom-info');
        this.canvasContainer = canvasContainer;

        // Cache search elements
        this.searchInputEl = this.controlsEl.querySelector('.pdf-search-input');
        this.searchClearBtnEl = this.controlsEl.querySelector('.pdf-search-clear');
        this.matchInfoEl = this.controlsEl.querySelector('.pdf-match-info');

        // Bind event handlers
        this.bindEvents();

        // Set up resize observer to refit PDF when container size changes
        this.resizeObserver = new ResizeObserver(() => this.scheduleViewerResize());
        this.resizeObserver.observe(this.container);

        // Apply persisted PDF reader settings (right margin gutter)
        this.applyPdfReaderSettings();
    }


    /**
     * Reads the persisted right-margin gutter (in px). Defaults to 0.
     * @returns {number}
     * @private
     */
    getRightMarginGutter() {
        return this.stateManager ? (this.stateManager.getRightMarginGutter()*10 || 0) : 0;
    }


    /**
     * Reads the persisted fit-size extra zoom (in percent) and returns the
     * scale multiplier (e.g. 10 -> 1.10). Defaults to 1.
     * @returns {number}
     * @private
     */
    getFitSizeZoomFactor() {
        const pct = this.stateManager ? (this.stateManager.getFitSizeZoom() || 0) : 0;
        return 1 + (pct / 100);
    }


    /**
     * Applies persisted PDF reader settings (right margin gutter) and re-fits
     * the page if the user is currently in a fit mode. Called on init and
     * whenever the settings dialog is saved.
     */
    applyPdfReaderSettings() {
        if (!this.mainContentEl) return;
        // Apply gutter outside the canvas-container so the canvas stays
        // horizontally centered and overflow (from fit-size zoom) is cropped
        // equally on both sides.
        const gutter = this.getRightMarginGutter();
        this.mainContentEl.style.paddingRight = gutter ? `${gutter}px` : '';
        // Re-fit if a PDF is loaded and we are in a fit mode.
        if (this.pdfDoc) {
            this.adjustToViewerSizeChanges();
        }
    }


    /**
     * Schedules a debounced resize adjustment for the PDF viewer.
     * Clears any pending timer and sets a new 500ms delay before adjusting.
     */
    scheduleViewerResize() {
        if (this.resizeTimerId) {
            clearTimeout(this.resizeTimerId);
        }
        this.resizeTimerId = setTimeout(() => {
            this.resizeTimerId = null;
            this.adjustToViewerSizeChanges();
        }, 500);
    }


    /**
     * Binds event handlers to control elements.
     * @private
     */
    bindEvents() {
        // Load PDF button
        this.controlsEl.querySelector('.pdf-load-pdf-btn').addEventListener('click', () => this.showLoadDialog());

        // Placeholder "click here" link (shown when no PDF is loaded)
        this.placeholderEl.querySelector('.pdf-placeholder-load-link').addEventListener('click', (e) => {
            e.preventDefault();
            this.showLoadDialog();
        });

        // Navigation buttons
        this.controlsEl.querySelector('.pdf-prev-btn').addEventListener('click', () => this.previousPage());
        this.controlsEl.querySelector('.pdf-next-btn').addEventListener('click', () => this.nextPage());

        // Page input
        this.pageInputEl.addEventListener('change', (e) => {
            const page = parseInt(e.target.value, 10);
            if (page >= 1 && page <= this.totalPages) {
                this.goToPage(page);
            } else {
                e.target.value = this.currentPage;
            }
        });

        // Zoom buttons
        this.controlsEl.querySelector('.pdf-zoom-out-btn').addEventListener('click', () => this.zoomOut());
        this.controlsEl.querySelector('.pdf-zoom-in-btn').addEventListener('click', () => this.zoomIn());
        this.controlsEl.querySelector('.pdf-fit-width-btn').addEventListener('click', () => this.fitToWidth());
        this.controlsEl.querySelector('.pdf-fit-page-btn').addEventListener('click', () => this.fitToPage());

        // Bookmark sidebar toggle
        this.controlsEl.querySelector('.pdf-sidebar-toggle-btn').addEventListener('click', () => this.toggleBookmarkSidebar());

        // Highlight-the-selection button. This is the only pointer-driven way to
        // highlight on a touch device that has neither a right-click nor Ctrl+Y,
        // so it must survive the press collapsing the selection: preventDefault on
        // pointerdown keeps the selection on screen, and highlightCachedSelection
        // falls back to the cached anchors if the browser dropped it anyway.
        this.highlightSelectionBtnEl = this.controlsEl.querySelector('.pdf-highlight-selection-btn');
        // mousedown, not pointerdown: preventing the default here is the standard way
        // to keep a selection alive across a button press, and doing it on mousedown
        // leaves touch input untouched (where suppressing defaults risks the click).
        this.highlightSelectionBtnEl.addEventListener('mousedown', (e) => e.preventDefault());
        this.highlightSelectionBtnEl.addEventListener('click', () => this.onHighlightButtonClicked());

        // Highlights list dialog
        this.controlsEl.querySelector('.pdf-highlights-list-btn').addEventListener('click', () => this.showHighlightsListDialog());

        // Either page highlight-indicator dot opens the same list
        for (const dot of this.pageHighlightIndicatorEls) {
            dot.addEventListener('click', () => this.showHighlightsListDialog());
        }

        // Search controls
        this.searchCleanup = setupTextSearchInput(
            this.searchInputEl,
            this.searchClearBtnEl,
            (value, isEnterKey) => {
                // (the box's text is compared the way performSearch keeps the query, trimmed and
                // lower case: compared as typed, Enter never stepped on for a query with a capital)
                if (isEnterKey && this.searchResults.length > 0 && value.trim().toLowerCase() === this.searchQuery) {
                    // Enter on existing search: go to next match
                    this.nextMatch();
                } else {
                    this.performSearch(value);
                }
            }
        );
        this.controlsEl.querySelector('.pdf-search-prev-btn').addEventListener('click', () => this.previousMatch());
        this.controlsEl.querySelector('.pdf-search-next-btn').addEventListener('click', () => this.nextMatch());

        // Focus viewer on click (needed for keyboard events)
        this.viewerEl.addEventListener('click', (e) => {
            // Don't steal focus from input elements
            const tagName = e.target.tagName.toLowerCase();
            if (tagName !== 'input' && tagName !== 'textarea' && tagName !== 'button') {
                this.viewerEl.focus();
            }
        });

        // Keyboard navigation (keys pressed while the focus is outside the viewer reach the same
        // handler through MapApp, see handleKeyFromOutside)
        this.viewerEl.addEventListener('keydown', (e) => this.handleReaderKey(e));

        // Mouse wheel page navigation
        let wheelCooldown = false;
        this.canvasContainer.addEventListener('wheel', (e) => {
            if (!this.pdfDoc || wheelCooldown) return;

            if (this.lastZoomMode === 'fitPage') {
                // we only handle mouse wheel on fit page
                if (e.deltaY < 0 && this.isScrolledToVerticalEdge(false)) {
                    e.preventDefault();
                    wheelCooldown = true;
                    this.previousPage();
                    setTimeout(() => { wheelCooldown = false; }, MAP_CONSTANTS.PDFVIEWER_MOUSEWHEEL_COOLDOWN);
                } else if (e.deltaY > 0 && this.isScrolledToVerticalEdge(true)) {
                    e.preventDefault();
                    this.nextPage();
                    wheelCooldown = true;
                    setTimeout(() => { wheelCooldown = false; }, MAP_CONSTANTS.PDFVIEWER_MOUSEWHEEL_COOLDOWN);
                }
            }
        });

        // Swipe left/right to turn the page (touch devices)
        this.bindSwipeNavigation();

        // Prevent erratic selection when clicking in empty space (margins,
        // gaps between spans). Without this, the browser falls back to
        // document order and anchors the selection at the top of the page.
        this.textLayerEl.addEventListener('mousedown', (e) => {
            // Primary button only. Preventing the default on a right-button mousedown
            // suppresses the context menu that would have followed it — including the
            // browser's own, which the Ctrl+right-click bypass depends on.
            if (e.button !== 0) return;
            if (e.target === this.textLayerEl) {
                e.preventDefault();
            }
        });

        // Ctrl+right-click hands the gesture to the browser's own menu (see the
        // contextmenu handler below). When it lands on a highlight with nothing
        // selected, the highlight's text is selected HERE, on mousedown, so that the
        // browser has it in hand by the time it builds the menu — changing the
        // selection during the contextmenu event instead can make that menu flicker
        // open and shut.
        this.textLayerEl.addEventListener('mousedown', (e) => {
            if (e.button !== 2 || !e.ctrlKey) return;
            if (this.getSelectedTextWithSpaces().trim()) return;
            const mark = e.target.closest ? e.target.closest('.pdf-highlight') : null;
            if (mark) this.selectHighlightTextById(mark.dataset.highlightId);
        });

        // Track how the page is being pressed. A touch/pen press means there is no
        // right-click and (usually) no keyboard, so a finished selection has to pop
        // its own action menu — see onSelectionChanged.
        this.textLayerEl.addEventListener('pointerdown', (e) => {
            this.lastPointerWasTouch = (e.pointerType === 'touch' || e.pointerType === 'pen');
            this.pointerDownPos = { x: e.clientX, y: e.clientY, t: Date.now() };
        });

        // Touch tap on an existing highlight opens the same menu the desktop gets
        // from right-clicking it — otherwise removing a highlight on a tablet is
        // only possible from the highlights list dialog.
        this.textLayerEl.addEventListener('pointerup', (e) => {
            const start = this.pointerDownPos;
            this.pointerDownPos = null;
            if (e.pointerType !== 'touch' && e.pointerType !== 'pen') return;
            // A short, stationary press only. A drag is a selection gesture, and a
            // long press belongs to the OS (or, on Android, to the contextmenu
            // handler below) — reacting to those too would double up on both.
            if (!start) return;
            if (Math.hypot(e.clientX - start.x, e.clientY - start.y) > PDF_TOUCH_TAP_SLOP_PX) return;
            if (Date.now() - start.t > PDF_TOUCH_TAP_MAX_MS) return;
            const sel = window.getSelection();
            if (sel && sel.rangeCount > 0 && !sel.isCollapsed) return;
            const hitMark = e.target.closest ? e.target.closest('.pdf-highlight') : null;
            if (!hitMark || !hitMark.dataset.highlightId) return;
            const rect = hitMark.getBoundingClientRect();
            this.showTextContextMenu(rect.left, rect.bottom + PDF_TOUCH_MENU_OFFSET_PX, '',
                hitMark.dataset.highlightId, null,
                { touchMode: true, avoidRect: rect, consumeNextClick: true });
        });

        // Keep the selection cache fresh (and, on touch, pop the action menu once
        // the selection settles). Selection events only fire on the document.
        this.selectionChangeHandler = () => this.onSelectionChanged();
        document.addEventListener('selectionchange', this.selectionChangeHandler);

        // Custom context menu for text selection and existing highlights
        this.textLayerEl.addEventListener('contextmenu', (e) => {
            // Ctrl+right-click yields to the browser's own menu. There is no way to OPEN
            // that menu from ours — a scripted contextmenu event is untrusted and
            // browsers ignore it — so standing aside is the only way to offer it at all.
            //
            // The modifier has to clear two bars, and Ctrl is the only one that does:
            //   Shift extends the selection to the click point, so the gesture meant to
            //     hand a selection to the browser reshapes it on the way. (Firefox
            //     bypasses page menus on Shift natively — its behavior, not ours, and it
            //     moves the selection for exactly this reason.)
            //   Alt dismisses menus on Windows, so the native menu opens on the click
            //     and closes again the moment the key comes up.
            // The selection for a bare highlight is prepared in the mousedown handler
            // above, not here — see the note there.
            if (e.ctrlKey) {
                this.hideTextContextMenu();
                return;   // no preventDefault: the native menu opens
            }

            const selectedText = this.getSelectedTextWithSpaces().trim();
            // Capture anchors NOW — clicking a menu item collapses the
            // selection before the menu's click handler reads it.
            const selectionAnchors = selectedText ? this.extractSelectionAnchors() : null;
            const hitMark = e.target.closest ? e.target.closest('.pdf-highlight') : null;
            const highlightId = hitMark ? hitMark.dataset.highlightId : null;

            if (!selectedText && !highlightId) return;

            e.preventDefault();
            // Android fires contextmenu on long-press, so this path can be a finger
            // as well as a right-click — size the rows accordingly.
            this.showTextContextMenu(e.clientX, e.clientY, selectedText, highlightId, selectionAnchors,
                { touchMode: this.lastPointerWasTouch, consumeNextClick: this.lastPointerWasTouch });
        });

        // Mirror clipboard copies (Ctrl+C / Cmd+C / right-click Copy) into the
        // play log with the same page+bookmark context as the menu's playlog action.
        // Do NOT preventDefault — let the browser perform the actual copy.
        this.textLayerEl.addEventListener('copy', () => {
            const selectedText = this.getSelectedTextWithSpaces().trim();
            if (!selectedText) return;
            this.logSelectionToPlayLog(selectedText, this.getSelectionPdfY(), this.getSelectionPdfX(), 'Copy');
            if (this.stateManager && this.stateManager.getPdfAutoHighlightOnCopy()) {
                // Capture anchors now (selection is still alive) but defer the
                // DOM mutation to the next tick so it can't disturb the
                // in-progress clipboard write.
                const anchors = this.extractSelectionAnchors();
                if (anchors) {
                    setTimeout(() => {
                        this.addHighlightFromAnchors(anchors, selectedText, /*silent*/ true);
                    }, 0);
                }
            }
        });

        // Dismiss context menu on click anywhere. A menu opened from a touch gesture
        // gets one free click: the tap that opened it still produces a click event,
        // which would otherwise close the menu before it could be read.
        // Kept on the instance so destroy() can take it down again (it lives on the document).
        this.documentDismissClickHandler = () => {
            // Time-boxed rather than a plain flag: if that opening tap never produces
            // a click, the grace period lapses on its own instead of eating the next
            // real dismissal.
            if (Date.now() < this.suppressDismissUntil) {
                this.suppressDismissUntil = 0;
                return;
            }
            this.hideTextContextMenu();
        };
        document.addEventListener('click', this.documentDismissClickHandler, true);

        // Drag and drop PDF files onto the canvas container
        this.bindDragAndDrop();
    }


    /**
     * The reader's keyboard shortcuts: the viewer element's keydown handler, also called by
     * handleKeyFromOutside for keys MapApp passes on while the focus is elsewhere.
     * @param {KeyboardEvent} e
     * @private
     */
    handleReaderKey(e) {
        // Ignore keyboard shortcuts when typing in input fields, but for the few that the
        // toolbar's own one-line boxes have no use for (PDF_KEYS_FROM_TOOLBAR_FIELDS)
        const tagName = e.target.tagName.toLowerCase();
        if (tagName === 'input' || tagName === 'textarea') {
            const isToolbarField = (e.target === this.pageInputEl || e.target === this.searchInputEl);
            if (!isToolbarField || !PDF_KEYS_FROM_TOOLBAR_FIELDS.has(e.key)) {
                return;
            }
        }

        switch (e.key) {
            // Page navigation
            case 'ArrowLeft':
            case 'PageUp':
                this.previousPage();
                e.preventDefault();
                break;
            case 'ArrowRight':
            case 'PageDown':
                this.nextPage();
                e.preventDefault();
                break;
            case 'Home':
                this.goToPage(1);
                e.preventDefault();
                break;
            case 'End':
                this.goToPage(this.totalPages);
                e.preventDefault();
                break;

            // Scrolling a page that does not fit in the view. Done here because the browser's
            // own arrow-key scrolling never reached the page area: it looks for something to
            // scroll from the focused element UPWARDS, and the focus sits on the viewer element,
            // an ancestor of the page area. At the top or bottom edge the key turns the page, in
            // every zoom mode (Oct 2026; it used to be fit-page only, the mouse wheel's rule, so in
            // fit-width a tall page just stopped at its bottom), and going back lands at the
            // previous page's BOTTOM, so reading on upwards stays continuous. A HELD key that has
            // scrolled a tall page to its edge stops there, and only a fresh press turns the page,
            // so holding the key cannot race on through the pages; in fit-page mode, where there
            // is nothing to scroll, a held key keeps turning pages as before. (With Ctrl/Alt/Meta
            // held the key is left alone.)
            case 'ArrowUp':
            case 'ArrowDown': {
                if (e.ctrlKey || e.altKey || e.metaKey) break;
                const down = (e.key === 'ArrowDown');
                if (this.pdfDoc && this.isScrolledToVerticalEdge(down)) {
                    if (e.repeat && this.lastZoomMode !== 'fitPage') {
                        // a held key stops at the edge (see above)
                    } else if (down) {
                        this.nextPage();
                    } else {
                        this.previousPage({ atBottom: true });
                    }
                } else {
                    this.canvasContainer.scrollBy({ top: down ? PDF_ARROW_KEY_SCROLL_PX : -PDF_ARROW_KEY_SCROLL_PX });
                }
                e.preventDefault();
                break;
            }

            // Zoom controls
            case '+':
            case '=':
                this.zoomIn();
                e.preventDefault();
                break;
            case '-':
            case '_':
                this.zoomOut();
                e.preventDefault();
                break;
            case '0':
                this.fitToPage();
                e.preventDefault();
                break;
            case 'w':
            case 'W':
                this.fitToWidth();
                e.preventDefault();
                break;

            // The bookmark sidebar has no key of its own here: Ctrl+B is handled in
            // MapApp.setupGlobalHotkeys (gated on the reader tab being on screen).
            // A bare "b" used to toggle it, which made the letter unusable for
            // anything else and fired on this switch even with Ctrl held.

            // Search shortcuts
            case 'f':
            case 'F':
                if (e.ctrlKey || e.metaKey) {
                    this.focusSearchInput();
                    e.preventDefault();
                }
                break;
            // Ctrl+G ("go to lead") is handled globally in MapApp's
            // setupGlobalHotkeys so it works from any tab / focus, not
            // just when the PDF viewer has focus.
            case 'y':
            case 'Y':
                if (e.ctrlKey || e.metaKey) {
                    this.highlightCachedSelection();
                    e.preventDefault();
                }
                break;
            case 'F2':
                if (e.shiftKey) {
                    this.previousHighlight();
                } else {
                    this.nextHighlight();
                }
                e.preventDefault();
                break;
            case 'F3':
                if ((e.target === this.searchInputEl) && (this.searchInputEl.value.trim().toLowerCase() !== this.searchQuery)) {
                    // from the search box, text not searched for yet (the search waits for a
                    // pause in the typing) is searched for first, as Enter would
                    this.performSearch(this.searchInputEl.value);
                } else if (e.shiftKey) {
                    // With an empty search bar these fall through to highlight
                    // navigation (see nextMatch/previousMatch).
                    this.previousMatch();
                } else {
                    this.nextMatch();
                }
                e.preventDefault();
                break;
            case 'Escape':
                if (this.searchQuery) {
                    this.clearSearch();
                    e.preventDefault();
                }
                break;
        }
    }


    /**
     * Sets up touch page-turn gestures over the page area: swipe left / right to
     * turn to the next / previous page, and double-tap on the right / left third
     * of the page area to do the same. Touch only — a mouse has the toolbar
     * arrows, the arrow keys and the scroll wheel already.
     *
     * Implemented with touch events rather than pointer events, deliberately: the
     * page area is a scrollable container, so the moment a finger moves the
     * browser claims the touch for native scrolling and fires pointercancel —
     * a pointerup-based flick detector never sees the end of the gesture (this is
     * exactly why the first pointer-event version of this feature never fired on
     * tablets). Touch events keep reporting through a native scroll, and every
     * listener here is passive, so the scroll itself is never delayed.
     *
     * The gestures deliberately give way to everything else the same finger could
     * mean: a second finger (pinch-zoom) cancels them, a slow drag is a pan or a
     * selection adjustment rather than a flick, a mostly-vertical move is scrolling,
     * and when the page is zoomed in far enough to scroll sideways the swipe pans
     * until the scroll actually reaches that edge — the same courtesy the wheel
     * handler shows vertical scrolling before it flips a page.
     * @private
     */
    bindSwipeNavigation() {
        const target = this.canvasContainer;

        target.addEventListener('touchstart', (e) => {
            if (e.touches.length > 1) {
                // A second finger landed: this is a pinch, not a swipe or a tap.
                this.swipeStart = null;
                this.swipeBlocked = true;
                return;
            }
            this.swipeBlocked = false;
            const touch = e.touches[0];
            this.swipeStart = {
                x: touch.clientX,
                y: touch.clientY,
                t: Date.now(),
                scrollLeft: target.scrollLeft,
                target: e.target
            };
        }, { passive: true });

        target.addEventListener('touchcancel', () => { this.swipeStart = null; });

        target.addEventListener('touchend', (e) => {
            const start = this.swipeStart;
            this.swipeStart = null;
            if (e.touches.length > 0) return;   // another finger is still down
            if (!start || this.swipeBlocked) return;
            if (!this.pdfDoc) return;

            const touch = e.changedTouches[0];
            if (!touch) return;
            const dx = touch.clientX - start.x;
            const dy = touch.clientY - start.y;
            const elapsed = Date.now() - start.t;

            // A short, stationary press is a tap — feed the double-tap detector
            // and leave swipe handling out of it.
            if (Math.hypot(dx, dy) <= PDF_TOUCH_TAP_SLOP_PX && elapsed <= PDF_TOUCH_TAP_MAX_MS) {
                this.handlePossiblePageTurnDoubleTap(touch.clientX, touch.clientY, start.target);
                return;
            }

            if (elapsed > PDF_SWIPE_MAX_MS) return;
            if (Math.abs(dx) < PDF_SWIPE_MIN_DISTANCE_PX) return;
            if (Math.abs(dx) < Math.abs(dy) * PDF_SWIPE_HORIZONTAL_RATIO) return;

            // If the container itself panned sideways during the gesture, the
            // finger was scrolling a zoomed-in page, not flicking.
            if (Math.abs(target.scrollLeft - start.scrollLeft) > PDF_SWIPE_SCROLL_EDGE_SLOP_PX) return;

            // Don't turn the page out from under a selection the user is working on.
            if (this.hasActiveTextLayerSelection()) return;

            // Swiping left (content moves left, dx < 0) reads forward.
            const goForward = dx < 0;
            if (!this.isScrolledToHorizontalEdge(goForward)) return;

            // The lift of a swipe still produces a click, which would follow any pdf
            // hyperlink the finger happened to pass over.
            this.suppressClickUntil = Date.now() + PDF_SWIPE_CLICK_SUPPRESS_MS;

            if (goForward) {
                this.nextPage();
            } else {
                this.previousPage();
            }
        }, { passive: true });

        target.addEventListener('click', (e) => {
            if (Date.now() >= this.suppressClickUntil) return;
            this.suppressClickUntil = 0;
            e.preventDefault();
            e.stopPropagation();
        }, true);
    }


    /**
     * Feeds one completed tap into the double-tap page-turn detector, and turns
     * the page when it completes a double tap on the left or right third of the
     * page area. Taps on things that already own a tap gesture — pdf hyperlinks,
     * existing highlights, the indicator dots, the context menu — never count,
     * and neither does any tap while a text selection is being worked on.
     * @param {number} x - Tap clientX
     * @param {number} y - Tap clientY
     * @param {EventTarget} targetEl - Element the gesture started on
     * @private
     */
    handlePossiblePageTurnDoubleTap(x, y, targetEl) {
        const prev = this.lastTouchTap;
        const now = Date.now();
        this.lastTouchTap = { x, y, t: now };
        if (!prev) return;
        if (now - prev.t > PDF_DOUBLE_TAP_MAX_MS) return;
        if (Math.hypot(x - prev.x, y - prev.y) > PDF_DOUBLE_TAP_SLOP_PX) return;
        // Consume the pair, so a triple tap can't turn two pages.
        this.lastTouchTap = null;

        if (targetEl && targetEl.closest
                && targetEl.closest('.pdf-annotation-link, .pdf-highlight, .pdf-page-highlight-indicator, .pdf-context-menu')) {
            return;
        }
        if (this.hasActiveTextLayerSelection()) return;

        const rect = this.canvasContainer.getBoundingClientRect();
        if (rect.width <= 0) return;
        const frac = (x - rect.left) / rect.width;
        if (frac >= 1 - PDF_DOUBLE_TAP_ZONE_FRACTION) {
            this.suppressClickUntil = Date.now() + PDF_SWIPE_CLICK_SUPPRESS_MS;
            this.nextPage();
        } else if (frac <= PDF_DOUBLE_TAP_ZONE_FRACTION) {
            this.suppressClickUntil = Date.now() + PDF_SWIPE_CLICK_SUPPRESS_MS;
            this.previousPage();
        }
        // Middle third: not a page-turn zone.
    }


    /**
     * Whether there is a live, non-collapsed text selection inside the pdf text layer.
     * @returns {boolean}
     * @private
     */
    hasActiveTextLayerSelection() {
        const sel = window.getSelection();
        return !!(sel && sel.rangeCount > 0 && !sel.isCollapsed
            && this.textLayerEl.contains(sel.getRangeAt(0).commonAncestorContainer));
    }


    /**
     * Whether the page area cannot scroll any further up (or down): the edge where the Up/Down
     * keys turn the page (in every zoom mode), and the mouse wheel does (in fit-page mode only).
     * @param {boolean} towardsBottom - True to test the bottom edge
     * @returns {boolean}
     * @private
     */
    isScrolledToVerticalEdge(towardsBottom) {
        const el = this.canvasContainer;
        return towardsBottom
            ? (el.scrollTop + el.clientHeight >= el.scrollHeight - 1)
            : (el.scrollTop <= 0);
    }


    /**
     * Whether the page area has run out of sideways scrolling in the given
     * direction — i.e. a horizontal swipe there can safely mean "turn the page"
     * instead of "pan the zoomed-in page".
     * @param {boolean} towardsRight - True to test the right-hand edge
     * @returns {boolean}
     * @private
     */
    isScrolledToHorizontalEdge(towardsRight) {
        const el = this.canvasContainer;
        const maxScroll = el.scrollWidth - el.clientWidth;
        if (maxScroll <= PDF_SWIPE_SCROLL_EDGE_SLOP_PX) return true;   // nothing to pan
        return towardsRight
            ? el.scrollLeft >= maxScroll - PDF_SWIPE_SCROLL_EDGE_SLOP_PX
            : el.scrollLeft <= PDF_SWIPE_SCROLL_EDGE_SLOP_PX;
    }


    /**
     * Sets up drag-and-drop event handlers for loading local PDF files.
     * Shows a visual overlay when dragging a file over the viewer area.
     * @private
     */
    bindDragAndDrop() {
        const dropTarget = this.canvasContainer;
        let dragCounter = 0;

        // Create drop overlay element
        this.dropOverlayEl = document.createElement('div');
        this.dropOverlayEl.className = 'pdf-drop-overlay';
        this.dropOverlayEl.innerHTML = '<i class="bi bi-file-earmark-pdf"></i><span>Drop PDF here</span>';
        this.dropOverlayEl.style.display = 'none';
        dropTarget.appendChild(this.dropOverlayEl);

        dropTarget.addEventListener('dragenter', (e) => {
            e.preventDefault();
            e.stopPropagation();
            dragCounter++;
            if (dragCounter === 1) {
                this.dropOverlayEl.style.display = '';
            }
        });

        dropTarget.addEventListener('dragover', (e) => {
            e.preventDefault();
            e.stopPropagation();
        });

        dropTarget.addEventListener('dragleave', (e) => {
            e.preventDefault();
            e.stopPropagation();
            dragCounter--;
            if (dragCounter <= 0) {
                dragCounter = 0;
                this.dropOverlayEl.style.display = 'none';
            }
        });

        dropTarget.addEventListener('drop', (e) => {
            e.preventDefault();
            e.stopPropagation();
            dragCounter = 0;
            this.dropOverlayEl.style.display = 'none';

            const files = e.dataTransfer.files;
            if (files.length === 0) return;

            const file = files[0];
            if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
                this.showError('Only PDF files are supported.');
                return;
            }

            const reader = new FileReader();
            reader.onload = async (ev) => {
                const data = new Uint8Array(ev.target.result);
                await this.loadPdfFromData(data, file.name, LEADS_SECTION_LABELS);
            };
            reader.readAsArrayBuffer(file);
        });
    }


    /**
     * Lazy-loads the self-hosted pdf.js library (/static/libs/pdfjs/<version>/;
     * see PDFJS_STATIC_DIR at the top of this file for the coupling note).
     * @returns {Promise<Object>} The pdfjsLib object
     * @private
     */
    async loadPdfJsLibrary() {
        if (this.isLibraryLoaded && this.pdfjsLib) {
            return this.pdfjsLib;
        }

        try {
            // Dynamically import pdf.js as ES module
            const pdfjsLib = await import(pdfjsScriptUrl());

            // Set the worker source
            pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl();

            this.pdfjsLib = pdfjsLib;
            this.isLibraryLoaded = true;

            return pdfjsLib;
        } catch (error) {
            console.error('Failed to load pdf.js library:', error);
            throw new Error('Failed to load PDF library');
        }
    }


    /**
     * Loads a PDF document from a URL.
     * @param {string} url - The URL of the PDF file to load
     * @param {string|Array<string>} [expandSectionLabelId] - Optional bookmark section label(s) to expand
     *   (see expandBookmarkByLabel; the loads of a case book pass LEADS_SECTION_LABELS)
     * @returns {Promise<boolean>} True only if the document loaded; false if the request was
     *   dropped because another load was in progress, or the load failed (the error is shown in
     *   the viewer). Callers that record the load (the Play Log) must check it.
     */
    async loadPdf(url, expandSectionLabelId, flagSkipRestoreSavedPage = false) {
        if (this.isLoading) {
            console.warn('PDF is already loading');
            return false;
        }

        this.isLoading = true;
        this.showLoading(true);
        this.hideError();
        let loaded = false;

        try {
            // Lazy-load pdf.js if not already loaded
            const pdfjsLib = await this.loadPdfJsLibrary();

            // Close previous document if any (stopping its renders first: they would go on drawing
            // pages of a destroyed document). pdf.js 6 removed PDFDocumentProxy.destroy(); a document
            // is destroyed through the loading task that opened it, here and below.
            await this.abandonPendingRenders();
            if (this.pdfDoc) {
                await this.pdfDoc.loadingTask.destroy();
                this.pdfDoc = null;
            }

            // Clear search state, text cache and outline cache for new document
            this.clearSearch();
            this.pageTextCache.clear();
            this.invalidateBookmarkCache();

            // Load the PDF document
            // (a parameter object: pdf.js 6 no longer takes a bare url)
            const loadingTask = pdfjsLib.getDocument({ url, ...pdfjsDocumentOptions() });
            this.pdfDoc = await loadingTask.promise;
            this.currentUrl = url;
            this.currentFileName = this.extractFilename(url);
            this.totalPages = this.pdfDoc.numPages;
            this.currentPage = 1;

            // Load persisted highlights for this URL into per-page map
            this.loadHighlightsForCurrentDoc();

            // Restore the last-viewed page for this doc (unless this load is a jump-to-location)
            this.restoreSavedPageIfAppropriate(flagSkipRestoreSavedPage);

            // Update UI
            this.placeholderEl.style.display = 'none';
            this.updateCurrentUrlDisplay(url);
            this.totalPagesEl.textContent = this.totalPages;
            this.pageInputEl.max = this.totalPages;

            // Render the (restored or first) page fitted to view
            await this.fitToPage();

            // Populate bookmark sidebar
            await this.populateBookmarkSidebar();

            if (expandSectionLabelId) {
                this.expandBookmarkByLabel(expandSectionLabelId);
            }
            loaded = true;

        } catch (error) {
            const errorMessage = `Failed to load PDF at "${url}."`;
            console.error(errorMessage, error);
            this.showError(errorMessage);
        } finally {
            this.isLoading = false;
            this.showLoading(false);
        }
        return loaded;
    }


    /**
     * Loads a PDF document from binary data (e.g., local file).
     * @param {ArrayBuffer} data - The PDF file data
     * @param {string} [fileName='Local File'] - Display name for the file
     * @param {string|Array<string>} [expandSectionLabelId] - Optional bookmark section label(s) to expand (see loadPdf)
     * @returns {Promise<boolean>} True only if the document loaded (see loadPdf)
     */
    async loadPdfFromData(data, fileName = 'Local File', expandSectionLabelId, flagSkipRestoreSavedPage = false) {
        if (this.isLoading) {
            console.warn('PDF is already loading');
            return false;
        }

        this.isLoading = true;
        this.showLoading(true);
        this.hideError();
        let loaded = false;

        try {
            // Lazy-load pdf.js if not already loaded
            const pdfjsLib = await this.loadPdfJsLibrary();

            // Close previous document if any (stopping its renders first, see loadPdf)
            await this.abandonPendingRenders();
            if (this.pdfDoc) {
                await this.pdfDoc.loadingTask.destroy();
                this.pdfDoc = null;
            }

            // Clear search state, text cache and outline cache for new document
            this.clearSearch();
            this.pageTextCache.clear();
            this.invalidateBookmarkCache();

            // Load the PDF document from data
            const loadingTask = pdfjsLib.getDocument({ data, ...pdfjsDocumentOptions() });
            this.pdfDoc = await loadingTask.promise;
            this.currentUrl = null; // No URL for local files
            this.currentFileName = fileName;
            this.totalPages = this.pdfDoc.numPages;

            // Local files persist under "local-file:<filename>" — same fileName
            // across sessions will reload the prior highlights.
            this.loadHighlightsForCurrentDoc();

            // Clear the saved last PDF URL since we loaded a local file
            if (this.stateManager) {
                this.stateManager.setLastPdfUrl('');
            }
            this.currentPage = 1;

            // Restore the last-viewed page for this doc (unless this load is a jump-to-location)
            this.restoreSavedPageIfAppropriate(flagSkipRestoreSavedPage);

            // Update UI (a local file's name is shown as it is, never parsed as a url)
            this.placeholderEl.style.display = 'none';
            this.updateCurrentUrlDisplay(fileName, fileName);
            this.totalPagesEl.textContent = this.totalPages;
            this.pageInputEl.max = this.totalPages;

            // Render the (restored or first) page fitted to view
            await this.fitToPage();

            // Populate bookmark sidebar
            await this.populateBookmarkSidebar();

            if (expandSectionLabelId) {
                this.expandBookmarkByLabel(expandSectionLabelId);
            }
            loaded = true;

        } catch (error) {
            console.error('Failed to load PDF data:', error);
            this.showError(`Failed to load PDF data: ${error.message}`);
        } finally {
            this.isLoading = false;
            this.showLoading(false);
        }
        return loaded;
    }


    /**
     * Shows the Load PDF dialog and loads the selected URL or file.
     * @returns {Promise<void>}
     */
    async showLoadDialog() {
        // Get history from state manager if available
        let urlHistory = [];
        let lastUrl = '';

        if (this.stateManager) {
            urlHistory = this.stateManager.getPdfUrlHistory() || [];
            lastUrl = this.stateManager.getLastPdfUrl() || '';
        }

        // If we have a current URL loaded, use that as default
        if (this.currentUrl) {
            lastUrl = this.currentUrl;
        }

        const result = await PdfUrlDialog.showPdfUrlDialog({
            urlHistory,
            lastUrl
        });

        if (result.action === 'load' && result.url) {
            // Save to history before loading
            if (this.stateManager) {
                this.stateManager.addPdfUrlToHistory(result.url);
            }
            await this.loadPdf(result.url, LEADS_SECTION_LABELS);
        } else if (result.action === 'loadFile' && result.data) {
            // Load from local file data
            await this.loadPdfFromData(result.data, result.fileName, LEADS_SECTION_LABELS);
        } else if (result.action === 'unload') {
            await this.clear();
        }
    }


    /**
     * Updates the current URL display element.
     * @param {string} url - The URL to display (its file name is shown, the whole url on hover)
     * @param {string} [displayName] - Text to show instead of the url's file name (a local file's name)
     * @private
     */
    updateCurrentUrlDisplay(url, displayName = null) {
        if (this.currentUrlEl) {
            if (url) {
                this.currentUrlEl.textContent = displayName || this.extractFilename(url);
                this.currentUrlEl.title = url; // Full URL on hover
            } else {
                this.currentUrlEl.textContent = '';
                this.currentUrlEl.title = '';
            }
        }
    }


    /**
     * The decoded file name of a url, for display; the whole url when its path ends in '/'.
     * @param {string} url - The URL to extract from
     * @returns {string}
     * @private
     */
    extractFilename(url) {
        return urlFileName(url, { decode: true }) || url;
    }


    /**
     * Renders a specific page of the PDF, at the current this.scale.
     * Calls may overlap (a quick second page turn, a refit while a page change is still drawing);
     * the LAST call wins: each call takes a generation number, and one that a newer call has
     * overtaken stops after every await, so its page or scale never ends up on screen. pdf.js
     * refuses a second render on a canvas that is still being drawn, so the running render is
     * cancelled and allowed to settle BEFORE the canvas is resized for the new one -- the second
     * render used to throw and be swallowed, losing page turns made within ~30 ms and leaving a
     * refit that raced a page change at the wrong scale.
     * @param {number} pageNum - The page number to render (1-indexed)
     * @returns {Promise<boolean>} True if this call drew the page and is still the newest one;
     *   false if a newer call overtook it, or the render failed (callers that mark something on
     *   the page afterwards must then leave it alone)
     * @private
     */
    async renderPage(pageNum) {
        if (!this.pdfDoc) return false;

        const generation = ++this.renderGeneration;
        const isSuperseded = () => (generation !== this.renderGeneration);
        this.renderTargetPage = pageNum;

        // A captured selection is anchored by text-item index within one page's text
        // layer, and the layer is about to be rebuilt — carrying the capture across a
        // render would let a later highlight land on whatever text now holds those
        // indices. Same for a menu anchored to the outgoing page.
        this.cachedSelection = null;
        this.clearSelectionTimers();
        this.hideTextContextMenu();
        this.updateHighlightSelectionButton(false);

        try {
            this.canvasWrapperEl.style.display = '';
            const page = await this.pdfDoc.getPage(pageNum);
            if (isSuperseded()) return false;

            // stop the render still drawing on the canvas (an older call's), and wait for it to let
            // go of the canvas: resizing a canvas pdf.js is drawing on is what broke the old code
            await this.cancelRunningRender();
            if (isSuperseded()) return false;

            const viewport = page.getViewport({ scale: this.scale });
            // PDF width in user units (same coordinate system as bookmark dest X).
            if (viewport.viewBox && viewport.viewBox.length >= 4) {
                this.currentPagePdfWidth = viewport.viewBox[2] - viewport.viewBox[0];
            }

            // Handle high-DPI displays for sharp rendering
            const canvas = this.canvasEl;
            const context = canvas.getContext('2d');
            const pixelRatio = window.devicePixelRatio || 1;

            // Set canvas internal dimensions scaled by pixel ratio
            canvas.width = Math.floor(viewport.width * pixelRatio);
            canvas.height = Math.floor(viewport.height * pixelRatio);

            // Set CSS display size to the intended dimensions
            canvas.style.width = `${Math.floor(viewport.width)}px`;
            canvas.style.height = `${Math.floor(viewport.height)}px`;

            // Scale the context to match pixel ratio
            context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

            // Render the page
            const renderContext = {
                canvasContext: context,
                viewport: viewport
            };

            const renderTask = page.render(renderContext);
            this.renderTask = renderTask;
            try {
                await renderTask.promise;
            } finally {
                if (this.renderTask === renderTask) {
                    this.renderTask = null;
                }
            }
            if (isSuperseded()) return false;

            // Update page info
            this.currentPage = pageNum;
            this.pageInputEl.value = pageNum;
            // remember this page for the current doc (no-op during the initial load render)
            this.persistCurrentPage();

            // Render text layer for text selection (transparent spans over canvas)
            await this.renderTextLayer(page, viewport, isSuperseded);
            if (isSuperseded()) return false;

            // Convert matching bookmark titles in the text layer to clickable hyperlinks
            this.linkBookmarkTitlesOnPage(pageNum);

            // Apply any persisted highlights for this page
            this.applyHighlightsToPage(pageNum);
            this.updatePageHighlightIndicator();

            // Render clickable annotation links on top of the canvas
            await this.renderAnnotationLinks(pageNum, isSuperseded);
            return !isSuperseded();

        } catch (error) {
            // a call a newer one overtook (its render cancelled, or its document closed by a new
            // load) has nothing to report; anything else is a real failure
            if (isSuperseded() || (error && error.name === 'RenderingCancelledException')) {
                return false;
            }
            console.error(`Failed to render page ${pageNum}: ${error}`, error);
            return false;
        } finally {
            if (!isSuperseded()) {
                this.renderTargetPage = null;
            }
        }
    }


    /**
     * Cancels the pdf.js render still drawing on the canvas, if any, and waits until it has
     * settled (rejected with RenderingCancelledException, or finished), so the canvas is free to
     * be resized and drawn on again.
     * @returns {Promise<void>}
     * @private
     */
    async cancelRunningRender() {
        const runningTask = this.renderTask;
        if (!runningTask) {
            return;
        }
        runningTask.cancel();
        try {
            await runningTask.promise;
        } catch (error) {
            // expected: RenderingCancelledException (any other failure is its own call's to report)
        }
        if (this.renderTask === runningTask) {
            this.renderTask = null;
        }
    }


    /**
     * Makes every renderPage call in progress stop (they are all superseded) and cancels the
     * running render; used before the document is closed (a new load, or clear), whose pages the
     * old calls would otherwise go on drawing from.
     * @returns {Promise<void>}
     * @private
     */
    async abandonPendingRenders() {
        this.renderGeneration++;
        this.renderTargetPage = null;
        await this.cancelRunningRender();
    }


    /**
     * Returns the page the viewer is on once pending renders finish: the page the newest
     * renderPage call is drawing, else the page on screen. Page turns and refits start from it,
     * so a quick second "next page" goes on to the page after the one still drawing, and a refit
     * during a page change redraws the NEW page.
     * @returns {number} The page number (1-indexed)
     * @private
     */
    getRenderTargetPage() {
        return this.renderTargetPage ?? this.currentPage;
    }


    /**
     * Renders clickable link annotations on top of the PDF canvas.
     * Extracts link annotations from the page and creates positioned overlay elements.
     * @param {number} pageNum - The page number (1-indexed)
     * @param {Function} [isSuperseded] - renderPage's test: true once a newer render has started,
     *   whose own link layer must not get this one's links added to it
     * @private
     */
    async renderAnnotationLinks(pageNum, isSuperseded = () => false) {
        this.annotationLayerEl.innerHTML = '';
        if (!this.pdfDoc) return;

        try {
            const page = await this.pdfDoc.getPage(pageNum);
            const annotations = await page.getAnnotations();
            if (isSuperseded()) return;
            const viewport = page.getViewport({ scale: this.scale });

            // Size the annotation layer to match the canvas CSS dimensions
            this.annotationLayerEl.style.width = this.canvasEl.style.width;
            this.annotationLayerEl.style.height = this.canvasEl.style.height;

            for (const annotation of annotations) {
                if (annotation.subtype !== 'Link') continue;
                if (!annotation.rect) continue;

                // Determine if this link has a navigable target
                if (!annotation.dest && !annotation.url) continue;

                // Convert PDF rect to viewport coordinates, one corner at a time (pdf.js 6.1 removed
                // PageViewport.convertToViewportRectangle, which did exactly this)
                const [x1, y1] = viewport.convertToViewportPoint(annotation.rect[0], annotation.rect[1]);
                const [x2, y2] = viewport.convertToViewportPoint(annotation.rect[2], annotation.rect[3]);

                // Normalize (viewport conversion may swap coordinates)
                const left = Math.min(x1, x2);
                const top = Math.min(y1, y2);
                const width = Math.abs(x2 - x1);
                const height = Math.abs(y2 - y1);

                const linkEl = document.createElement('a');
                linkEl.className = 'pdf-annotation-link';
                linkEl.style.left = `${left}px`;
                linkEl.style.top = `${top}px`;
                linkEl.style.width = `${width}px`;
                linkEl.style.height = `${height}px`;

                if (annotation.dest) {
                    // Internal PDF destination
                    linkEl.href = '#';
                    linkEl.addEventListener('click', async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        await this.navigateToDestination(annotation.dest);
                        // Notify listener of bookmark click
                        if (this.onBookmarkClick) {
                            const id = (typeof annotation.dest === 'string') ? annotation.dest : null;
                            const normalizedLabel = this.lookUpBookmarkTitleFromId(id);
                            let annotationPageNum = null;
                            try {
                                const destArray = (typeof annotation.dest === 'string')
                                    ? await this.pdfDoc.getDestination(annotation.dest)
                                    : annotation.dest;
                                if (Array.isArray(destArray) && destArray[0]) {
                                    const pageIndex = await this.pdfDoc.getPageIndex(destArray[0]);
                                    annotationPageNum = pageIndex + 1;
                                }
                            } catch (e) {
                                // Could not resolve page
                            }
                            this.onBookmarkClick(normalizedLabel, false, annotationPageNum);
                        }
                    });
                } else if (annotation.url) {
                    // External URL
                    linkEl.href = annotation.url;
                    linkEl.target = '_blank';
                    linkEl.rel = 'noopener noreferrer';
                    linkEl.title = annotation.url;
                    linkEl.addEventListener('click', (e) => {
                        // /cases/mviewer/ links wrap a media file for standalone pdf readers; unwrap to the
                        // raw file url so playlists/audio/images/video get the native handling below. Files
                        // with no special handling here (.md, .pdf, unknown) match none of the branches and
                        // fall through to the anchor's original href — the mviewer page in a new tab.
                        const url = PdfViewer.resolveMviewerFileUrl(annotation.url) || annotation.url;
                        // .m3u playlist links: hand the url to the host so it is added/selected/played in
                        // the dedicated "Background Music" tab, leaving the reader free to keep reading.
                        if (PdfViewer.isPlaylistUrl(url)) {
                            e.preventDefault();
                            e.stopPropagation();
                            if (this.onPlaylistMedia) {
                                this.onPlaylistMedia(url);
                            } else {
                                this.showMediaInPopup(PdfViewer.stripBackgroundLoop(url));
                            }
                            return;
                        }
                        // Audio links (and legacy ?backgroundLoop links to audio/video): add to the User
                        // Playlist and play in the Background Music tab. backgroundLoop forces single-track loop.
                        if (PdfViewer.isAudioUrl(url) || PdfViewer.hasBackgroundLoop(url)) {
                            e.preventDefault();
                            e.stopPropagation();
                            const loop = PdfViewer.hasBackgroundLoop(url);
                            const cleanUrl = PdfViewer.stripBackgroundLoop(url);
                            if (this.onTrackMedia) {
                                this.onTrackMedia(cleanUrl, { loop });
                            } else {
                                this.showMediaInPopup(cleanUrl);
                            }
                            return;
                        }
                        // Other media (images/video): keep the in-page popup behavior, gated by the setting.
                        const popupEnabled = this.stateManager && this.stateManager.getPdfPopupMediaFiles();
                        if (popupEnabled && PdfViewer.isMediaUrl(url)) {
                            e.preventDefault();
                            e.stopPropagation();
                            this.showMediaInPopup(url);
                        }
                    });
                }

                this.annotationLayerEl.appendChild(linkEl);
            }
        } catch (error) {
            console.error('Failed to render annotation links:', error);
        }
    }


    // ==========================================
    // TEXT LAYER (SELECTION SUPPORT)
    // ==========================================

    /**
     * Renders a text layer of transparent positioned spans over the canvas
     * for native text selection and copy. Uses getTextContent() data to
     * create spans, then adjusts horizontal scaling to match rendered widths.
     * @param {Object} page - The pdf.js page object
     * @param {Object} viewport - The pdf.js viewport for current scale
     * @param {Function} [isSuperseded] - renderPage's test: true once a newer render has started,
     *   whose own text layer must not get this one's spans appended to it
     * @private
     */
    async renderTextLayer(page, viewport, isSuperseded = () => false) {
        this.textLayerEl.innerHTML = '';

        try {
            const textContent = await page.getTextContent();
            if (isSuperseded()) return;

            // Detect multi-column layout from the actual rendered text items
            // (not bookmark presence — a column may have no bookmarks).
            this.currentPageColumnInfo = this._detectMultiColumnLayout(textContent.items, this.currentPagePdfWidth);

            // Size the text layer to match the canvas CSS dimensions (not internal pixel dimensions)
            this.textLayerEl.style.width = this.canvasEl.style.width;
            this.textLayerEl.style.height = this.canvasEl.style.height;

            const scale = viewport.scale;
            const pendingWidthAdjustments = [];
            const spanInfos = [];

            for (let itemIdx = 0; itemIdx < textContent.items.length; itemIdx++) {
                const item = textContent.items[itemIdx];
                if (!item.str) continue;

                const tx = item.transform;
                // Font size from the transformation matrix
                const fontHeight = Math.hypot(tx[0], tx[1]);
                // Convert PDF position (origin bottom-left) to viewport coordinates (origin top-left)
                const [viewX, viewY] = viewport.convertToViewportPoint(tx[4], tx[5]);

                const span = document.createElement('span');
                span.textContent = item.str;
                span.dataset.pdfX = tx[4];
                span.dataset.pdfY = tx[5];
                span.dataset.itemIdx = itemIdx;
                const scaledFontHeight = fontHeight * scale;
                span.style.fontSize = `${scaledFontHeight}px`;
                span.style.left = `${viewX}px`;
                const topPos = viewY - scaledFontHeight;
                span.style.top = `${topPos}px`;

                this.textLayerEl.appendChild(span);

                spanInfos.push({ span, topPos, scaledFontHeight, leftPos: viewX });

                if (item.width > 0) {
                    pendingWidthAdjustments.push({ span, targetWidth: item.width * scale });
                }
            }

            // Extend span heights to fill gaps between text lines.
            // Without explicit heights, the gaps between absolutely-positioned
            // spans are dead zones where click-drag selection falls back to
            // document order, causing selection to start from the page top.
            //
            // The gap for each span is computed by finding the next span
            // downward that's on the SAME SIDE of the page midline (or of
            // the layout-detected column boundary when available). This
            // independently handles the case of a short right column above
            // a long left column: a left-column span looks past the still-
            // active right-column lines and finds its next same-column line,
            // even when column detection itself didn't trigger (e.g. because
            // the right column was too short to satisfy the detector's
            // minimum-line-count threshold).
            if (spanInfos.length > 1) {
                const textLayerWidthPx = parseFloat(this.textLayerEl.style.width) || 0;
                let sideBoundary = textLayerWidthPx > 0 ? textLayerWidthPx / 2 : null;
                if (this.currentPageColumnInfo
                        && this.currentPageColumnInfo.multiColumn
                        && this.currentPageColumnInfo.columnBoundary != null) {
                    const [bx] = viewport.convertToViewportPoint(this.currentPageColumnInfo.columnBoundary, 0);
                    sideBoundary = bx;
                }
                const sideOf = (info) => {
                    if (sideBoundary === null) return 0;
                    return info.leftPos < sideBoundary ? 0 : 1;
                };

                const sortedByY = [...spanInfos].sort((a, b) => a.topPos - b.topPos);
                const indexByInfo = new Map();
                for (let i = 0; i < sortedByY.length; i++) indexByInfo.set(sortedByY[i], i);

                for (const info of spanInfos) {
                    const startIdx = indexByInfo.get(info);
                    const mySide = sideOf(info);
                    let gap = null;
                    // Walk forward in Y order; skip same-line items (within 3px
                    // of current Y) and items on the other side of the midline.
                    for (let j = startIdx + 1; j < sortedByY.length; j++) {
                        const candidate = sortedByY[j];
                        if (candidate.topPos - info.topPos < 3) continue;
                        if (sideOf(candidate) !== mySide) continue;
                        gap = candidate.topPos - info.topPos;
                        break;
                    }
                    // Use the side-local gap when reasonable; fall back to a
                    // safe default so the height never collapses too short.
                    // Overlap with the next line is harmless because same-side
                    // spans are consecutive in DOM and the next one sits on top.
                    const minGap = info.scaledFontHeight * 0.5;
                    const gapHeight = (gap && gap >= minGap) ? gap : info.scaledFontHeight * 1.3;
                    const minHeight = info.scaledFontHeight * 1.5;
                    info.span.style.height = `${Math.max(gapHeight, minHeight)}px`;
                }
            }

            // Second pass: adjust horizontal scaling so spans match the PDF's rendered text width
            for (const { span, targetWidth } of pendingWidthAdjustments) {
                const actualWidth = span.offsetWidth;
                if (actualWidth > 0) {
                    span.style.transform = `scaleX(${targetWidth / actualWidth})`;
                }
            }

            // Add filler spans to prevent erratic selection when dragging past
            // text edges. Two-pass approach:
            //   Pass 1: Group spans by Y, split into segments at large X gaps,
            //           and collect gutter positions from multi-segment lines.
            //   Pass 2: Create filler spans bounded to each segment's column area.
            //           On two-column pages, single-segment lines (where only one
            //           column has text) are bounded to the gutter — without this,
            //           their fillers would span the full page width, and because
            //           right-column fillers come later in DOM they'd sit on top of
            //           left-column text spans, intercepting the cursor during drag.
            const layerRect = this.textLayerEl.getBoundingClientRect();
            const textLayerWidth = layerRect.width;
            if (textLayerWidth > 0 && spanInfos.length > 0) {
                const segmentGapThreshold = Math.max(10, textLayerWidth * 0.02);

                // Group spans by Y position with tolerance-based merging.
                // On two-column pages, left and right column spans at the same
                // visual line can differ by 1-3px. Strict rounding would put them
                // in separate groups, preventing segment splitting from detecting
                // the column gutter. Sort by Y and merge spans within 3px.
                const lineGroups = new Map();
                const sortedByY = [...spanInfos].sort((a, b) => a.topPos - b.topPos);
                let groupKey = null;
                for (const info of sortedByY) {
                    const y = Math.round(info.topPos);
                    if (groupKey === null || y - groupKey > 3) {
                        groupKey = y;
                    }
                    if (!lineGroups.has(groupKey)) lineGroups.set(groupKey, []);
                    lineGroups.get(groupKey).push(info);
                }

                // Pass 1: compute segments per line and collect gutter positions
                const lineData = [];
                const gutterMids = [];

                for (const [lineTop, lineSpans] of lineGroups) {
                    const measured = lineSpans.map(info => {
                        const rect = info.span.getBoundingClientRect();
                        return {
                            span: info.span,
                            left: rect.left - layerRect.left,
                            right: rect.right - layerRect.left,
                            height: parseFloat(info.span.style.height) || info.scaledFontHeight
                        };
                    }).sort((a, b) => a.left - b.left);

                    const segments = [[measured[0]]];
                    for (let i = 1; i < measured.length; i++) {
                        const gap = measured[i].left - measured[i - 1].right;
                        if (gap > segmentGapThreshold) {
                            segments.push([]);
                        }
                        segments[segments.length - 1].push(measured[i]);
                    }

                    // Record gutter midpoints from multi-segment lines
                    if (segments.length >= 2) {
                        for (let i = 1; i < segments.length; i++) {
                            const prevRight = segments[i - 1][segments[i - 1].length - 1].right;
                            const nextLeft = segments[i][0].left;
                            gutterMids.push((prevRight + nextLeft) / 2);
                        }
                    }

                    lineData.push({ lineTop, segments });
                }

                // Compute consensus gutter position. Prefer the median of
                // gutter midpoints derived from multi-segment lines (most
                // accurate when columns share lines). When no multi-segment
                // lines exist — e.g. a short right column whose Y values
                // don't align with any left-column line — fall back to the
                // layout-detected column boundary. Without this fallback,
                // single-segment lines in either column would get full-page-
                // width fillers, and the right column's filler would cover
                // the left column area at its Y, intercepting clicks intended
                // for the left column's text.
                gutterMids.sort((a, b) => a - b);
                let gutterX = gutterMids.length > 0
                    ? gutterMids[Math.floor(gutterMids.length / 2)]
                    : null;
                if (gutterX === null
                        && this.currentPageColumnInfo
                        && this.currentPageColumnInfo.multiColumn
                        && this.currentPageColumnInfo.columnBoundary != null) {
                    const [bx] = viewport.convertToViewportPoint(this.currentPageColumnInfo.columnBoundary, 0);
                    gutterX = bx;
                }

                // Pass 2: create fillers with gutter-aware boundaries
                for (const { lineTop, segments } of lineData) {
                    for (let segIdx = 0; segIdx < segments.length; segIdx++) {
                        const seg = segments[segIdx];
                        const segLeft = seg[0].left;
                        const segRight = seg[seg.length - 1].right;
                        const segHeight = seg[0].height;
                        const firstSpan = seg[0].span;
                        const lastSpan = seg[seg.length - 1].span;

                        let leftBound, rightBound;

                        if (segments.length >= 2) {
                            // Multi-segment line: use midpoints between segments
                            leftBound = segIdx > 0
                                ? (segments[segIdx - 1][segments[segIdx - 1].length - 1].right + segLeft) / 2
                                : 0;
                            rightBound = segIdx < segments.length - 1
                                ? (segRight + segments[segIdx + 1][0].left) / 2
                                : textLayerWidth;
                        } else if (gutterX !== null) {
                            // Single-segment line on a multi-column page:
                            // bound fillers to this segment's column side of the gutter
                            const segCenter = (segLeft + segRight) / 2;
                            if (segCenter < gutterX) {
                                leftBound = 0;
                                rightBound = gutterX;
                            } else {
                                leftBound = gutterX;
                                rightBound = textLayerWidth;
                            }
                        } else {
                            // Single-column page: fillers extend to page edges
                            leftBound = 0;
                            rightBound = textLayerWidth;
                        }

                        if (segLeft - leftBound > 2) {
                            const filler = document.createElement('span');
                            filler.textContent = ' ';
                            filler.style.left = `${leftBound}px`;
                            filler.style.top = `${lineTop}px`;
                            filler.style.width = `${segLeft - leftBound}px`;
                            filler.style.height = `${segHeight}px`;
                            filler.style.fontSize = '1px';
                            firstSpan.before(filler);
                        }

                        const remaining = rightBound - segRight;
                        if (remaining > 2) {
                            const filler = document.createElement('span');
                            filler.textContent = ' ';
                            filler.style.left = `${segRight}px`;
                            filler.style.top = `${lineTop}px`;
                            filler.style.width = `${remaining}px`;
                            filler.style.height = `${segHeight}px`;
                            filler.style.fontSize = '1px';
                            lastSpan.after(filler);
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Failed to render text layer:', error);
        }
    }


    /**
     * Scans the text layer for bookmark titles and adds a clickable map marker icon next to each match.
     * Called after renderTextLayer for each page render.
     * @param {number} pageNum - The current page number (1-indexed)
     * @private
     */
    linkBookmarkTitlesOnPage(pageNum) {
        if (!this.shouldLinkPdfBookmark || this.bookmarkCache.length === 0) return;

        // Collect normalized bookmark titles on this page that should be linked
        const linkedTitles = new Set();
        const collectLinkedTitles = (items) => {
            for (const bookmark of items) {
                if (bookmark.page === pageNum) {
                    const bookmarkTitleNormalized = getDefaultLeadIdPatterner().normalizeLeadId(bookmark.title);
                    if (bookmarkTitleNormalized && this.shouldLinkPdfBookmark(bookmarkTitleNormalized)) {
                        linkedTitles.add(bookmarkTitleNormalized);
                    }
                }
                if (bookmark.children && bookmark.children.length > 0) {
                    collectLinkedTitles(bookmark.children);
                }
            }
        };
        collectLinkedTitles(this.bookmarkCache);

        if (linkedTitles.size === 0) return;

        // Scan text layer spans for matching text and add map marker icons
        const spans = this.textLayerEl.querySelectorAll('span');
        const layerRect = this.textLayerEl.getBoundingClientRect();

        for (const span of spans) {
            const spanTextNormalized = getDefaultLeadIdPatterner().normalizeLeadId(span.textContent);
            if (spanTextNormalized && linkedTitles.has(spanTextNormalized)) {
                const spanRect = span.getBoundingClientRect();
                // Use the span's original font size (not its extended height
                // which includes dead-zone coverage below the text)
                const spanFontSize = parseFloat(span.style.fontSize) || spanRect.height;

                // Create clickable map marker icon positioned to the right of the title
                const iconEl = document.createElement('i');
                iconEl.className = 'bi bi-geo-alt-fill pdf-titleText-mapIcon';
                iconEl.title = `Visit ${spanTextNormalized} on map`;
                const iconSize = spanFontSize * 0.60;
                iconEl.style.left = `${spanRect.right - layerRect.left + 2}px`;
                iconEl.style.top = `${spanRect.top - layerRect.top + spanFontSize - iconSize}px`;
                iconEl.style.fontSize = `${iconSize}px`;

                iconEl.addEventListener('click', (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    this.userClicksOnHyperlinkedBookmarkTitle(spanTextNormalized, pageNum);
                });

                this.textLayerEl.appendChild(iconEl);
            }
        }
    }




    // ==========================================
    // PERSISTENT TEXT HIGHLIGHTS
    // ==========================================

    /**
     * Returns the storage key used to persist highlights for the currently
     * loaded document. URL-loaded PDFs are keyed by URL; locally-loaded files
     * (drag-and-drop / file picker) are keyed by "local-file:<filename>" so
     * they don't collide with same-named URLs.
     * @returns {string|null} The key, or null if no document is loaded.
     * @private
     */
    getHighlightStorageKeyForCurrentDoc() {
        if (this.currentUrl) return this.currentUrl;
        if (this.currentFileName) return `local-file:${this.currentFileName}`;
        return null;
    }


    /**
     * Loads persisted highlights for the current document into the per-page
     * in-memory map. Keyed by URL for URL-loaded PDFs and by filename for
     * local files.
     * @private
     */
    loadHighlightsForCurrentDoc() {
        this.pageHighlights.clear();
        this.currentHighlightNavId = null;
        this.highlightsByDocUrl = this.getHighlightStorageKeyForCurrentDoc();
        if (!this.highlightsByDocUrl) return;

        const highlights = this.highlightStore.loadForUrl(this.highlightsByDocUrl);
        for (const h of highlights) {
            if (!this.pageHighlights.has(h.pageNum)) {
                this.pageHighlights.set(h.pageNum, []);
            }
            this.pageHighlights.get(h.pageNum).push(h);
        }
    }


    /**
     * Flushes the current in-memory highlights to the store for the current URL.
     * @private
     */
    persistHighlights() {
        if (!this.highlightsByDocUrl) return;
        const all = [];
        for (const list of this.pageHighlights.values()) {
            for (const h of list) all.push(h);
        }
        this.highlightStore.saveForUrl(this.highlightsByDocUrl, all);
    }


    /**
     * Persists the current page for the current document (stored in the same per-doc location as
     * highlights), so it can be restored when the doc is reloaded. Skipped while a load is in
     * progress so the initial render never clobbers the saved value we are about to restore — only
     * genuine user navigation persists.
     * @private
     */
    persistCurrentPage() {
        if (this.isLoading) return;
        if (!this.highlightsByDocUrl) return;
        this.highlightStore.savePageForUrl(this.highlightsByDocUrl, this.currentPage);
    }


    /**
     * If a saved last-viewed page exists for the current document, sets currentPage to it so the
     * initial render lands there. Skipped when the load is part of a jump-to-location action (the
     * caller will navigate to its own target instead).
     * @param {boolean} flagSkip - When true, leaves currentPage unchanged (no restore)
     * @private
     */
    restoreSavedPageIfAppropriate(flagSkip) {
        if (flagSkip) return;
        if (!this.highlightsByDocUrl) return;
        const savedPage = this.highlightStore.loadPageForUrl(this.highlightsByDocUrl);
        if (Number.isInteger(savedPage) && savedPage >= 1 && savedPage <= this.totalPages) {
            this.currentPage = savedPage;
        }
    }


    /**
     * Renders highlight wrappers (<mark.pdf-highlight>) onto the text-layer
     * spans for the given page, replacing any previously rendered wrappers.
     * Overlapping highlights collapse onto the first-encountered id per char.
     * @param {number} pageNum
     */
    applyHighlightsToPage(pageNum) {
        const spans = this.textLayerEl.querySelectorAll('span[data-item-idx]');

        // Clear prior marks: any item-idx span that already has highlight wrappers
        // is flattened back to plain text. (Item-idx spans never hold other element
        // children, so this is safe — filler spans and bookmark icons live as
        // siblings, not descendants.)
        for (const span of spans) {
            if (span.querySelector('.pdf-highlight')) {
                span.textContent = span.textContent;
            }
        }

        const highlights = this.pageHighlights.get(pageNum);
        if (!highlights || highlights.length === 0) return;

        // Build a per-item-index list of (start, end, id) ranges.
        // end === null means "extends through end of this span".
        // The walk is clamped to the item indexes this page really has: a stored highlight whose
        // range runs far past them (one imported before the store validated ranges) once walked a
        // billion indexes and ran the tab out of memory, on every visit (second code study 12.1).
        let lastItemIdx = -1;
        for (const span of spans) {
            const spanIdx = parseInt(span.dataset.itemIdx, 10);
            if (spanIdx > lastItemIdx) lastItemIdx = spanIdx;
        }
        const rangesByIdx = new Map();
        for (const h of highlights) {
            // Notes are not pinned to PDF text and have no positional anchors;
            // a null startItemIdx would otherwise satisfy `null <= null`, so skip them.
            if (h.kind === 'note') continue;
            const firstIdx = Math.max(0, h.startItemIdx);
            const lastIdx = Math.min(lastItemIdx, h.endItemIdx);
            for (let idx = firstIdx; idx <= lastIdx; idx++) {
                const start = (idx === h.startItemIdx) ? h.startOffset : 0;
                const end = (idx === h.endItemIdx) ? h.endOffset : null;
                if (!rangesByIdx.has(idx)) rangesByIdx.set(idx, []);
                rangesByIdx.get(idx).push({ start, end, id: h.id });
            }
        }

        for (const span of spans) {
            const idx = parseInt(span.dataset.itemIdx, 10);
            const ranges = rangesByIdx.get(idx);
            if (!ranges || ranges.length === 0) continue;

            const text = span.textContent;
            const len = text.length;
            if (len === 0) continue;

            // For each character position, record the first highlight id covering it.
            const charIds = new Array(len).fill(null);
            for (const r of ranges) {
                const s = Math.max(0, Math.min(len, r.start));
                const e = Math.max(0, Math.min(len, r.end == null ? len : r.end));
                for (let i = s; i < e; i++) {
                    if (charIds[i] === null) charIds[i] = r.id;
                }
            }

            // Rebuild span: contiguous runs of same id form one node.
            span.textContent = '';
            let i = 0;
            while (i < len) {
                const currentId = charIds[i];
                let j = i;
                while (j < len && charIds[j] === currentId) j++;
                const chunk = text.substring(i, j);
                if (currentId === null) {
                    span.appendChild(document.createTextNode(chunk));
                } else {
                    const mark = document.createElement('mark');
                    mark.className = 'pdf-highlight';
                    mark.dataset.highlightId = currentId;
                    mark.textContent = chunk;
                    span.appendChild(mark);
                }
                i = j;
            }
        }
    }


    /**
     * Captures the current text selection on the visible page and stores it
     * as a persistent highlight. No-op if there is no usable selection.
     * @returns {boolean} True if a highlight was added
     */
    addHighlightFromSelection() {
        const anchors = this.extractSelectionAnchors();
        const text = this.getSelectedTextWithSpaces().trim();
        return this.addHighlightFromAnchors(anchors, text);
    }


    /**
     * Orders two (textItemIndex, characterOffset) positions within a page's text layer.
     * @returns {number} <0, 0 or >0 in the usual comparator sense
     * @private
     */
    _comparePosition(idxA, offA, idxB, offB) {
        if (idxA !== idxB) return idxA - idxB;
        return offA - offB;
    }


    /**
     * Whether range `inner` lies entirely within range `outer`. Both are anchor
     * objects ({startItemIdx, startOffset, endItemIdx, endOffset}); identical ranges
     * count as within, which is what makes re-selecting a highlight exactly recognise it.
     * @private
     */
    _isRangeWithin(inner, outer) {
        return this._comparePosition(outer.startItemIdx, outer.startOffset,
                                     inner.startItemIdx, inner.startOffset) <= 0
            && this._comparePosition(inner.endItemIdx, inner.endOffset,
                                     outer.endItemIdx, outer.endOffset) <= 0;
    }


    /**
     * Returns the highlights on a page that carry a text range — i.e. everything the
     * containment tests below can meaningfully compare against. Notes are pinned to a
     * page rather than to text and have no anchors at all, so they are never included.
     * @private
     */
    _anchoredHighlightsOnPage(pageNum) {
        const list = this.pageHighlights.get(pageNum);
        if (!list) return [];
        return list.filter(h => h.kind !== 'note' && typeof h.startItemIdx === 'number');
    }


    /**
     * The existing highlight that fully contains the given range, if any. A selection
     * inside a highlight means the user is pointing at that highlight rather than
     * asking for a new one.
     * @param {Object|null} anchors
     * @param {number} [pageNum=this.currentPage]
     * @returns {Object|null}
     */
    findHighlightContainingRange(anchors, pageNum = this.currentPage) {
        if (!anchors) return null;
        return this._anchoredHighlightsOnPage(pageNum)
            .find(h => this._isRangeWithin(anchors, h)) || null;
    }


    /**
     * Selects the on-page text of a highlight, as though the user had dragged across
     * it. Used when a gesture is about to hand off to something that works on the
     * browser selection (the native context menu) but nothing is selected yet.
     * @param {string} id - Highlight id
     * @returns {boolean} True if the highlight had marks on the current page to select
     */
    selectHighlightTextById(id) {
        if (!id || !this.textLayerEl) return false;
        const marks = this.textLayerEl.querySelectorAll(
            `mark.pdf-highlight[data-highlight-id="${CSS.escape(id)}"]`);
        if (marks.length === 0) return false;

        // One highlight can be split across several marks (it wraps lines, or straddles
        // text items), and they sit in reading order in the DOM — so the whole thing is
        // the span from before the first to after the last.
        const range = document.createRange();
        range.setStartBefore(marks[0]);
        range.setEndAfter(marks[marks.length - 1]);

        const sel = window.getSelection();
        if (!sel) return false;
        sel.removeAllRanges();
        sel.addRange(range);
        return true;
    }


    /**
     * Looks up a stored highlight or note by id, across all pages.
     * @param {string} id
     * @returns {Object|null}
     */
    getHighlightById(id) {
        if (!id) return null;
        for (const list of this.pageHighlights.values()) {
            const found = list.find(h => h.id === id);
            if (found) return found;
        }
        return null;
    }


    /**
     * The existing highlights that lie entirely within the given range — the ones a
     * new highlight over that range would swallow.
     * @param {Object|null} anchors
     * @param {number} [pageNum=this.currentPage]
     * @returns {Array<Object>}
     */
    findHighlightsWithinRange(anchors, pageNum = this.currentPage) {
        if (!anchors) return [];
        return this._anchoredHighlightsOnPage(pageNum)
            .filter(h => this._isRangeWithin(h, anchors));
    }


    /**
     * The toolbar highlight button. With a selection (live or cached) in hand it
     * highlights / un-highlights it as always; with nothing selected it opens the
     * notes dialog straight into adding a fresh note (the Ctrl+L behavior), so the
     * button is never a dead end on a touch device.
     * @private
     */
    onHighlightButtonClicked() {
        const liveText = this.getSelectedTextWithSpaces().trim();
        if (liveText || this.cachedSelection) {
            this.highlightCachedSelection();
        } else {
            this.showHighlightsListDialog({ autoAddNote: true });
        }
    }


    /**
     * Highlights the live selection, or — when it has already been collapsed —
     * the last one captured by onSelectionChanged. This is what every button and
     * hotkey path uses: pressing a button collapses the selection before the click
     * handler runs, and on touch the OS drops it at times of its own choosing, so
     * reading window.getSelection() alone loses the selection roughly half the time.
     * @returns {boolean} True if a highlight was added
     */
    highlightCachedSelection() {
        const liveAnchors = this.extractSelectionAnchors();
        const liveText = this.getSelectedTextWithSpaces().trim();
        const useLive = !!(liveAnchors && liveText);

        const anchors = useLive ? liveAnchors : (this.cachedSelection ? this.cachedSelection.anchors : null);
        const text = useLive ? liveText : (this.cachedSelection ? this.cachedSelection.text : '');
        if (!anchors || !text) {
            this.showWarningToast('Select some text on the page first, then highlight it.');
            return false;
        }

        // Selecting text that is already highlighted is how you point at a highlight,
        // so the button un-highlights it rather than stacking a second one on top.
        const existing = this.findHighlightContainingRange(anchors);
        let acted;
        if (existing) {
            acted = this.removeHighlight(existing.id);
            const sel = window.getSelection();
            if (sel) sel.removeAllRanges();
        } else {
            acted = this.addHighlightFromAnchors(anchors, text);
        }

        if (acted) {
            // Drop the capture so a second press can't act on the same text twice.
            this.cachedSelection = null;
            this.clearSelectionTimers();
            this.updateHighlightSelectionButton(false);
        }
        return acted;
    }


    /**
     * Captures the current text-layer selection and, on touch, schedules the
     * selection action menu. Runs on every document selectionchange.
     *
     * A collapsed selection deliberately does NOT clear the cache — collapsing is
     * exactly what happens when the user reaches for the highlight button, and the
     * cache is what makes that button work. It is cleared instead at the two points
     * where it could become wrong: a page render (anchors are per-page item indices)
     * and a successful highlight.
     * @private
     */
    onSelectionChanged() {
        this.clearSelectionTimers();

        const sel = window.getSelection();
        const hasRange = !!(sel && sel.rangeCount > 0 && !sel.isCollapsed);
        if (!hasRange || !this.textLayerEl
                || !this.textLayerEl.contains(sel.getRangeAt(0).commonAncestorContainer)) {
            return;
        }

        // The selection moved, so any menu anchored to where it used to be is stale.
        this.hideTextContextMenu();

        // Both of these are deferred, because selectionchange fires continuously
        // while a selection is being dragged and resolving anchors means walking
        // every text span on the page. Nothing depends on the capture being
        // instantaneous: highlightCachedSelection reads the live selection first
        // and only falls back to the capture once the browser has dropped it.
        this.selectionCaptureTimerId = setTimeout(() => {
            this.selectionCaptureTimerId = null;
            this.captureCurrentSelection();
        }, PDF_SELECTION_CAPTURE_DELAY_MS);

        if (this.shouldAutoShowSelectionMenu()) {
            this.selectionMenuTimerId = setTimeout(() => {
                this.selectionMenuTimerId = null;
                this.showSelectionMenuForCurrentSelection();
            }, PDF_TOUCH_SELECTION_MENU_DELAY_MS);
        }
    }


    /**
     * Resolves the live selection into the anchor capture the button paths read.
     * @private
     */
    captureCurrentSelection() {
        const text = this.getSelectedTextWithSpaces().trim();
        const anchors = text ? this.extractSelectionAnchors() : null;
        if (anchors) {
            this.cachedSelection = {
                anchors,
                text,
                pdfY: this.getSelectionPdfY(),
                pdfX: this.getSelectionPdfX()
            };
        }
        this.updateHighlightSelectionButton(!!(anchors || this.cachedSelection));
    }


    /**
     * Cancels any pending selection capture / menu popup.
     * @private
     */
    clearSelectionTimers() {
        if (this.selectionCaptureTimerId) {
            clearTimeout(this.selectionCaptureTimerId);
            this.selectionCaptureTimerId = null;
        }
        if (this.selectionMenuTimerId) {
            clearTimeout(this.selectionMenuTimerId);
            this.selectionMenuTimerId = null;
        }
    }


    /**
     * Whether a finished selection should pop its own action menu. True for touch
     * and pen input, since neither can reach the right-click menu or Ctrl+Y. Mouse
     * users keep the quieter right-click-only behavior.
     * @returns {boolean}
     * @private
     */
    shouldAutoShowSelectionMenu() {
        if (this.lastPointerWasTouch) return true;
        // Covers the first selection of a session, before any pointerdown has
        // told us what kind of device this is.
        return !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
    }


    /**
     * Shows the selection action menu anchored to the current selection rather
     * than to a cursor position (there is no cursor on touch).
     * @private
     */
    showSelectionMenuForCurrentSelection() {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
        const range = sel.getRangeAt(0);
        if (!this.textLayerEl || !this.textLayerEl.contains(range.commonAncestorContainer)) return;

        const text = this.getSelectedTextWithSpaces().trim();
        const anchors = text ? this.extractSelectionAnchors() : null;
        if (!anchors) return;

        // Anchor below the selection; showTextContextMenu flips it above when there
        // is no room. avoidRect keeps it off the selected text itself, which on
        // touch is also where the OS draws its selection handles.
        const rect = range.getBoundingClientRect();
        this.showTextContextMenu(rect.left, rect.bottom + PDF_TOUCH_MENU_OFFSET_PX, text, null, anchors,
            { touchMode: true, avoidRect: rect });
    }


    /**
     * Updates the toolbar highlight button's icon and tooltip for the current
     * selection state. The button is always fully enabled: with a selection it
     * highlights (or, over an existing highlight, un-highlights), and with
     * nothing selected it opens the notes dialog to add a note instead.
     * @private
     */
    updateHighlightSelectionButton(hasSelection) {
        const btn = this.highlightSelectionBtnEl;
        if (!btn) return;

        // Say which of the things the press will do. Over text that is already
        // highlighted the button un-highlights, so it wears an eraser there rather
        // than letting the user find out by pressing it.
        const removeMode = !!(hasSelection && this.cachedSelection
            && this.findHighlightContainingRange(this.cachedSelection.anchors));
        const icon = btn.querySelector('i');
        if (icon) {
            icon.className = removeMode ? 'bi bi-eraser' : 'bi bi-highlighter';
        }
        btn.title = removeMode
            ? 'Remove the highlight from the selected text (Ctrl+Y)'
            : 'Highlight the selected text (Ctrl+Y) — with nothing selected, add a note';
    }


    /**
     * Stores a persistent highlight from previously-captured anchors and text.
     * Used by the right-click menu, where the selection must be captured at
     * contextmenu time (clicking a menu item collapses the selection).
     * @param {Object|null} anchors - {startItemIdx, startOffset, endItemIdx, endOffset}
     * @param {string} text - Plain-text content of the selection
     * @param {boolean} [silent=false] - When true, skip the "PDF Highlight"
     *   play-log entry. Used by auto-highlight-on-copy, where a "PDF Copy"
     *   entry has already been written for the same selection.
     * @returns {boolean} True if a highlight was added
     */
    addHighlightFromAnchors(anchors, text, silent = false) {
        if (!anchors) return false;
        if (!text) return false;

        // Highlights never nest. A range already inside one adds nothing — this is the
        // path auto-highlight-on-copy takes when the same passage is copied twice, and
        // without the guard each copy would stack another mark on the same words.
        if (this.findHighlightContainingRange(anchors)) return false;

        // Resolve pdfY/pdfX + nearest-prior bookmark from the start span now,
        // so the values are frozen on the highlight record (the highlights-
        // list dialog can render them without re-reading other pages' text).
        // pdfX enables column-aware bookmark matching on multi-column pages.
        const startSpan = this.textLayerEl.querySelector(`span[data-item-idx="${anchors.startItemIdx}"]`);
        const pdfY = (startSpan && startSpan.dataset.pdfY != null) ? parseFloat(startSpan.dataset.pdfY) : null;
        const pdfX = (startSpan && startSpan.dataset.pdfX != null) ? parseFloat(startSpan.dataset.pdfX) : null;
        const bookmarkLabel = this.findNearestPriorBookmarkLabel(this.currentPage, pdfY, pdfX, this.currentPageColumnInfo);

        const h = {
            id: this.generateHighlightId(),
            kind: 'highlight',
            pageNum: this.currentPage,
            startItemIdx: anchors.startItemIdx,
            startOffset: anchors.startOffset,
            endItemIdx: anchors.endItemIdx,
            endOffset: anchors.endOffset,
            text: text,
            pdfY: pdfY,
            pdfX: pdfX,
            bookmarkLabel: bookmarkLabel,
            createdAt: Date.now()
        };

        if (!this.pageHighlights.has(this.currentPage)) {
            this.pageHighlights.set(this.currentPage, []);
        }
        const pageList = this.pageHighlights.get(this.currentPage);

        // A wider highlight drawn over narrower ones replaces them: the page ends up
        // with one mark covering the passage rather than a mark inside a mark. Computed
        // before the new highlight joins the list so it cannot swallow itself.
        for (const swallowed of this.findHighlightsWithinRange(anchors)) {
            const i = pageList.indexOf(swallowed);
            if (i >= 0) pageList.splice(i, 1);
            if (swallowed.id === this.currentHighlightNavId) {
                // The F2 cursor pointed at a highlight that no longer exists.
                this.currentHighlightNavId = null;
            }
        }

        pageList.push(h);

        // Clear any active selection so the new highlight is the only visual cue.
        const sel = window.getSelection();
        if (sel) sel.removeAllRanges();

        this.applyHighlightsToPage(this.currentPage);
        this.updatePageHighlightIndicator();
        this.persistHighlights();
        if (!silent) {
            this.logSelectionToPlayLog(text, pdfY, pdfX, 'Highlight');
        }
        return true;
    }


    /**
     * Creates a freeform user note pinned to the current page (not to any PDF text)
     * and stores it alongside highlights. The note's lead is the nearest-prior
     * bookmark for the current page, and its text starts empty (filled in by the
     * inline editor in the highlights-list dialog). Notes carry kind:'note' so the
     * rest of the pipeline can skip the on-page mark rendering that real highlights use.
     * @returns {string} The new note's id (so the caller can focus its editable cell)
     */
    addNote() {
        const bookmarkLabel = this.findNearestPriorBookmarkLabel(this.currentPage, null, null, this.currentPageColumnInfo);
        const note = {
            id: this.generateHighlightId(),
            kind: 'note',
            pageNum: this.currentPage,
            text: '',
            pdfY: null,
            pdfX: null,
            bookmarkLabel: bookmarkLabel,
            createdAt: Date.now()
        };

        if (!this.pageHighlights.has(this.currentPage)) {
            this.pageHighlights.set(this.currentPage, []);
        }
        this.pageHighlights.get(this.currentPage).push(note);

        // A note has nothing to mark on the page, so no applyHighlightsToPage call —
        // the corner indicator is the only on-page trace it leaves.
        this.updatePageHighlightIndicator();
        this.persistHighlights();
        return note.id;
    }


    /**
     * Updates the text of an existing note record and persists the change. When the
     * text actually changed to a non-empty value, also records a "PDF Note" play-log
     * entry (using the note's own page + lead, since editing may happen while a
     * different page is on screen).
     * @param {string} id - The note's id
     * @param {string} text - The new note text
     * @returns {boolean} True if the note was found and updated
     */
    updateNoteText(id, text) {
        if (!id) return false;
        for (const list of this.pageHighlights.values()) {
            const note = list.find(h => h.id === id);
            if (note) {
                const changed = note.text !== text;
                note.text = text;
                this.persistHighlights();
                if (changed && note.pageNum === this.currentPage) {
                    // The indicator tooltip quotes the note text, so it goes stale
                    // the moment the note is edited in the highlights-list dialog.
                    this.updatePageHighlightIndicator();
                }
                if (changed && text && this.onCopyToPlayLog) {
                    this.onCopyToPlayLog(text, note.pageNum, note.bookmarkLabel || null, 'Note');
                }
                return true;
            }
        }
        return false;
    }


    /**
     * Returns a flat array of all highlights in the currently loaded doc,
     * sorted newest-first by createdAt.
     * @returns {Array<Object>}
     */
    getAllHighlights() {
        const all = [];
        for (const list of this.pageHighlights.values()) {
            for (const h of list) all.push(h);
        }
        all.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        return all;
    }


    /**
     * Removes every highlight from the currently loaded doc, persists the
     * empty state, and clears the visible marks on the active page.
     */
    deleteAllHighlights() {
        this.pageHighlights.clear();
        this.currentHighlightNavId = null;
        this.applyHighlightsToPage(this.currentPage);
        this.updatePageHighlightIndicator();
        this.persistHighlights();
    }


    /**
     * Builds a serializable export payload of all highlights in the current doc, with a small
     * envelope (version, timestamp, source) for the highlights import/export feature.
     * @returns {{version: number, exportedAt: string, pdfUrl: (string|null), highlights: Array<Object>}}
     */
    getHighlightsExportData() {
        return {
            version: 1,
            exportedAt: new Date().toISOString(),
            pdfUrl: this.highlightsByDocUrl || this.currentUrl || null,
            highlights: this.getAllHighlights()
        };
    }


    /**
     * Validates and normalizes a raw imported highlight object. Returns a clean highlight record, or
     * null if the object is missing the required positional fields / text.
     * @param {Object} raw
     * @returns {Object|null}
     * @private
     */
    _sanitizeImportedHighlight(raw) {
        // one validator for every way highlights arrive from a file (it lives with the stored format)
        return PdfHighlightStore.sanitizeHighlight(raw);
    }


    /**
     * Returns true if the raw object is a structurally valid importable highlight.
     * @param {Object} raw
     * @returns {boolean}
     */
    isValidImportedHighlight(raw) {
        return this._sanitizeImportedHighlight(raw) !== null;
    }


    /**
     * Merges imported highlights into the current document. Accepts either a bare array of highlights
     * or an export envelope ({highlights: [...]}). Duplicates (same page + offsets + text) are skipped,
     * and ids are regenerated when missing or colliding with an existing highlight. Persists and
     * re-renders the active page when anything was added.
     * @param {Object|Array} parsed - Parsed JSON (envelope or array)
     * @returns {{added: number, skipped: number, error: (string|null)}}
     */
    importHighlightsData(parsed) {
        if (!this.highlightsByDocUrl) {
            return { added: 0, skipped: 0, error: 'No PDF is currently loaded.' };
        }
        const incoming = Array.isArray(parsed)
            ? parsed
            : (parsed && Array.isArray(parsed.highlights) ? parsed.highlights : null);
        if (!incoming) {
            return { added: 0, skipped: 0, error: 'No "highlights" array found in the data.' };
        }

        // Notes have no positional anchors, so dedup them by id; highlights dedup by position+text.
        const keyOf = (h) => (h.kind === 'note')
            ? 'note|' + h.id
            : [h.pageNum, h.startItemIdx, h.startOffset, h.endItemIdx, h.endOffset, h.text].join('|');
        const existing = this.getAllHighlights();
        const seen = new Set(existing.map(keyOf));
        const usedIds = new Set(existing.map(h => h.id));

        let added = 0;
        let skipped = 0;
        for (const raw of incoming) {
            const h = this._sanitizeImportedHighlight(raw);
            if (!h) { skipped++; continue; }
            const k = keyOf(h);
            if (seen.has(k)) { skipped++; continue; }
            seen.add(k);
            // ensure a unique id (regenerate if missing or already used)
            if (!h.id || usedIds.has(h.id)) { h.id = this.generateHighlightId(); }
            usedIds.add(h.id);
            if (!this.pageHighlights.has(h.pageNum)) { this.pageHighlights.set(h.pageNum, []); }
            this.pageHighlights.get(h.pageNum).push(h);
            added++;
        }

        if (added > 0) {
            this.persistHighlights();
            this.applyHighlightsToPage(this.currentPage);
            this.updatePageHighlightIndicator();
        }
        return { added, skipped, error: null };
    }


    /**
     * Navigates to the highlight's page and flashes the marked span(s) to
     * draw the eye. If the page isn't currently rendered, renders it first
     * (renderPage triggers applyHighlightsToPage as part of its flow, so the
     * mark elements will exist by the time this awaits).
     * @param {Object} highlight - The highlight record
     * @returns {Promise<boolean>}
     */
    async goToHighlight(highlight) {
        if (!this.pdfDoc || !highlight) return false;
        if (highlight.pageNum < 1 || highlight.pageNum > this.totalPages) return false;

        // (also while any render is pending, which would replace the page and its marks after this
        // flashed them)
        if (highlight.pageNum !== this.currentPage || this.renderTargetPage !== null) {
            // false = a newer page request overtook this one: its page is the one on screen, so
            // there is nothing of this highlight's to flash
            if (!(await this.renderPage(highlight.pageNum))) {
                return false;
            }
        }

        // Notes are pinned to a page, not to text, so there is no mark to scroll to
        // or flash. Landing on the page and pulsing the corner dot is the whole of
        // "go to" for them.
        if (highlight.kind === 'note') {
            this.flashPageHighlightIndicator();
            return true;
        }

        const marks = this.textLayerEl.querySelectorAll(`mark.pdf-highlight[data-highlight-id="${CSS.escape(highlight.id)}"]`);
        if (marks.length === 0) return false;

        // Bring the first mark into view, then flash all of them.
        marks[0].scrollIntoView({ block: 'center', behavior: 'auto' });
        for (const mark of marks) {
            mark.classList.remove('pdf-highlight-flash');
            void mark.offsetWidth;  // force reflow so the animation restarts
            mark.classList.add('pdf-highlight-flash');
        }
        setTimeout(() => {
            for (const mark of marks) mark.classList.remove('pdf-highlight-flash');
        }, 1600);
        return true;
    }


    /**
     * Returns all highlights and notes for the current doc in reading order:
     * by page, then down the page, with a page's notes after its highlights
     * (a note has no position on the page, so there is nowhere else to put it).
     * This is the order F2 / Shift+F2 step through.
     * @returns {Array<Object>}
     */
    getHighlightsInDocumentOrder() {
        const all = [];
        for (const list of this.pageHighlights.values()) {
            for (const h of list) all.push(h);
        }
        all.sort((a, b) => {
            if (a.pageNum !== b.pageNum) return a.pageNum - b.pageNum;
            const aIsNote = (a.kind === 'note');
            const bIsNote = (b.kind === 'note');
            if (aIsNote !== bIsNote) return aIsNote ? 1 : -1;
            // Within a page, higher pdfY is further up the page (PDF origin is
            // bottom-left), so descending pdfY reads top-to-bottom.
            const aY = (typeof a.pdfY === 'number') ? a.pdfY : null;
            const bY = (typeof b.pdfY === 'number') ? b.pdfY : null;
            if (aY !== null && bY !== null && aY !== bY) return bY - aY;
            // Fall back to text-item order for highlights that predate pdfY, then
            // to creation time so the order is always total and stable.
            const aIdx = (typeof a.startItemIdx === 'number') ? a.startItemIdx : null;
            const bIdx = (typeof b.startItemIdx === 'number') ? b.startItemIdx : null;
            if (aIdx !== null && bIdx !== null && aIdx !== bIdx) return aIdx - bIdx;
            return (a.createdAt || 0) - (b.createdAt || 0);
        });
        return all;
    }


    /**
     * Steps to the next or previous highlight/note in document order, wrapping at
     * the ends. Starting fresh (or after the remembered one was deleted), "next"
     * picks the first highlight at or after the current page and "previous" the
     * last one at or before it, so navigation begins from where the reader is
     * rather than from the top of the document.
     * @param {number} direction - +1 for next, -1 for previous
     * @returns {Promise<boolean>} True if a highlight was navigated to
     * @private
     */
    async stepHighlight(direction) {
        if (!this.pdfDoc) return false;
        const ordered = this.getHighlightsInDocumentOrder();
        if (ordered.length === 0) {
            this.showWarningToast('No highlights or notes in this document');
            return false;
        }

        let index = this.currentHighlightNavId
            ? ordered.findIndex(h => h.id === this.currentHighlightNavId)
            : -1;

        if (index < 0) {
            // No cursor: enter the list from the current page.
            if (direction > 0) {
                const at = ordered.findIndex(h => h.pageNum >= this.currentPage);
                index = (at >= 0) ? at : 0;
            } else {
                let at = -1;
                for (let i = ordered.length - 1; i >= 0; i--) {
                    if (ordered[i].pageNum <= this.currentPage) { at = i; break; }
                }
                index = (at >= 0) ? at : ordered.length - 1;
            }
        } else {
            index = (index + direction + ordered.length) % ordered.length;
        }

        const target = ordered[index];
        this.currentHighlightNavId = target.id;
        await this.goToHighlight(target);
        this.updateHighlightNavInfo(index + 1, ordered.length);
        return true;
    }


    /**
     * Navigates to the next highlight or note (wraps around).
     * @returns {Promise<boolean>}
     */
    async nextHighlight() {
        return this.stepHighlight(1);
    }


    /**
     * Navigates to the previous highlight or note (wraps around).
     * @returns {Promise<boolean>}
     */
    async previousHighlight() {
        return this.stepHighlight(-1);
    }


    /**
     * Reports highlight-navigation position in the toolbar's match-info slot (shared
     * with search, which is idle whenever this is in use). Cleared by the next search.
     * @param {number} current - 1-based position
     * @param {number} total
     * @private
     */
    updateHighlightNavInfo(current, total) {
        if (!this.matchInfoEl) return;
        this.matchInfoEl.textContent = `Highlight ${current} of ${total}`;
        this.matchInfoEl.classList.remove('no-matches');
    }


    /**
     * True when the search bar holds a query (typed or already searched). Used to
     * decide whether the match-navigation controls act on search results or fall
     * back to stepping through highlights.
     * @returns {boolean}
     * @private
     */
    hasSearchText() {
        if (this.searchQuery) return true;
        return !!(this.searchInputEl && this.searchInputEl.value.trim());
    }


    /**
     * Shows or hides the corner dot that marks the current page as carrying
     * highlights and/or notes, and keeps its tooltip counts current. Called after
     * every render and after anything that changes the highlight set.
     * @private
     */
    updatePageHighlightIndicator() {
        const dots = this.pageHighlightIndicatorEls;
        if (!dots || dots.length === 0) return;

        const onPage = this.pdfDoc ? (this.pageHighlights.get(this.currentPage) || []) : [];
        if (onPage.length === 0) {
            for (const dot of dots) dot.style.display = 'none';
            return;
        }

        const notes = onPage.filter(h => h.kind === 'note');
        const highlightCount = onPage.length - notes.length;
        const parts = [];
        if (highlightCount > 0) parts.push(`${highlightCount} highlight${highlightCount === 1 ? '' : 's'}`);
        if (notes.length > 0) parts.push(`${notes.length} note${notes.length === 1 ? '' : 's'}`);
        const lines = [`${parts.join(' and ')} on this page — click to open the list`];

        // Spell out the notes themselves, one per tooltip line: a note leaves no mark
        // on the page, so without this the dot says one exists but never what it says.
        // Highlights are deliberately not listed — their text is already on the page.
        // Each note is flattened to a single line and clipped so the tooltip stays
        // readable no matter what was typed into it.
        const writtenNotes = notes.filter(n => n.text && n.text.trim());
        for (const note of writtenNotes.slice(0, PDF_INDICATOR_MAX_TOOLTIP_NOTES)) {
            lines.push(`• ${this.truncateText(note.text.trim().replace(/\s+/g, ' '), PDF_INDICATOR_MAX_TOOLTIP_NOTE_CHARS)}`);
        }
        const overflow = writtenNotes.length - PDF_INDICATOR_MAX_TOOLTIP_NOTES;
        if (overflow > 0) {
            lines.push(`…and ${overflow} more note${overflow === 1 ? '' : 's'}`);
        }

        const title = lines.join('\n');
        for (const dot of dots) {
            dot.title = title;
            dot.style.display = '';
        }
    }


    /**
     * Pulses the page's highlight-indicator dot. This is the "you have arrived"
     * cue when navigating to a note, which has no on-page mark to flash.
     * @private
     */
    flashPageHighlightIndicator() {
        for (const dot of (this.pageHighlightIndicatorEls || [])) {
            if (dot.style.display === 'none') continue;
            dot.classList.remove('pdf-page-highlight-indicator-flash');
            void dot.offsetWidth;  // force reflow so the animation restarts
            dot.classList.add('pdf-page-highlight-indicator-flash');
            setTimeout(() => dot.classList.remove('pdf-page-highlight-indicator-flash'), 1600);
        }
    }


    /**
     * Opens the highlights-list dialog for the current doc.
     * @param {Object} [options={}] - Display options
     * @param {boolean} [options.autoAddNote=false] - When true, the dialog adds and focuses a fresh note on open (Ctrl+L).
     */
    showHighlightsListDialog(options = {}) {
        PdfHighlightsListDialog.showDialog({ viewer: this, autoAddNote: options.autoAddNote || false });
    }


    /**
     * Removes the highlight with the given id from in-memory state and the store.
     * @param {string} highlightId
     * @returns {boolean} True if the highlight was found and removed
     */
    removeHighlight(highlightId) {
        if (!highlightId) return false;
        for (const [pageNum, list] of this.pageHighlights) {
            const i = list.findIndex(h => h.id === highlightId);
            if (i >= 0) {
                list.splice(i, 1);
                if (list.length === 0) this.pageHighlights.delete(pageNum);
                if (highlightId === this.currentHighlightNavId) {
                    // Drop the nav cursor so the next F2 re-enters from the current page.
                    this.currentHighlightNavId = null;
                }
                if (pageNum === this.currentPage) {
                    this.applyHighlightsToPage(pageNum);
                    this.updatePageHighlightIndicator();
                }
                this.persistHighlights();
                return true;
            }
        }
        return false;
    }


    /**
     * Resolves the currently selected Range to (itemIdx, charOffset) anchors
     * within the text layer of the current page. Works for selections that
     * cross multiple spans/lines and for selections whose endpoints land in
     * filler spans (which have no data-item-idx).
     *
     * Strategy: find every item-idx span the range intersects, then compute
     * the start/end character offsets only on the boundary spans. Interior
     * spans are implicitly fully covered.
     *
     * @returns {{startItemIdx:number, startOffset:number, endItemIdx:number, endOffset:number}|null}
     * @private
     */
    extractSelectionAnchors() {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return null;
        const range = sel.getRangeAt(0);

        // querySelectorAll returns spans in DOM order, which matches itemIdx order
        // (renderTextLayer appends item-idx spans sequentially before fillers are inserted).
        const itemSpans = this.textLayerEl.querySelectorAll('span[data-item-idx]');
        const hits = [];
        for (const span of itemSpans) {
            if (range.intersectsNode(span)) hits.push(span);
        }
        if (hits.length === 0) return null;

        const firstSpan = hits[0];
        const lastSpan = hits[hits.length - 1];

        const startOffset = this._offsetWithinSpan(firstSpan, range.startContainer, range.startOffset, /*fallback*/ 0);
        const endOffset = this._offsetWithinSpan(lastSpan, range.endContainer, range.endOffset, /*fallback*/ lastSpan.textContent.length);

        const startItemIdx = parseInt(firstSpan.dataset.itemIdx, 10);
        const endItemIdx = parseInt(lastSpan.dataset.itemIdx, 10);
        if (Number.isNaN(startItemIdx) || Number.isNaN(endItemIdx)) return null;

        if (startItemIdx === endItemIdx && startOffset >= endOffset) return null;

        return { startItemIdx, startOffset, endItemIdx, endOffset };
    }


    /**
     * Returns the character offset within `span`'s textContent corresponding
     * to a Range point (container, offset). If the point is outside the span
     * (e.g., in a filler before/after it), returns `fallback`.
     * @private
     */
    _offsetWithinSpan(span, container, offset, fallback) {
        if (!span.contains(container) && container !== span) return fallback;
        const measure = document.createRange();
        measure.setStart(span, 0);
        measure.setEnd(container, offset);
        return measure.toString().length;
    }


    /**
     * Generates a short unique id for a highlight.
     * @private
     */
    generateHighlightId() {
        return 'h_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
    }




    /**
     * Shows the custom context menu at the given position. Items are built
     * based on what is available: text selection (Copy / Search / Highlight /
     * Copy to play log) and/or a clicked existing highlight (Remove highlight).
     * @param {number} clientX - Mouse x position (viewport coordinates)
     * @param {number} clientY - Mouse y position (viewport coordinates)
     * @param {string} selectedText - The selected text (may be empty)
     * @param {string|null} highlightId - Existing highlight under the cursor, if any
     * @param {Object|null} selectionAnchors - Captured selection anchors {startItemIdx, startOffset, endItemIdx, endOffset}
     * @param {Object} [options={}] - Placement options
     * @param {boolean} [options.touchMode=false] - Render with finger-sized rows
     * @param {DOMRect|null} [options.avoidRect=null] - Viewport rect the menu should not cover
     *   (the selection or tapped highlight it belongs to); the menu flips above it when needed.
     * @param {boolean} [options.consumeNextClick=false] - Set when opening from a gesture that
     *   will still produce a click, so the dismiss handler ignores that one click.
     * @private
     */
    showTextContextMenu(clientX, clientY, selectedText, highlightId, selectionAnchors, options = {}) {
        const menu = this.contextMenuEl;

        // Selecting text that is already highlighted means the user is pointing at that
        // highlight: "Highlight selection" there could only nest a duplicate, so the
        // menu drops it and offers removal of the highlight they landed inside instead.
        // This also covers right-clicking a highlight without selecting anything, where
        // the caller passes the id it hit directly.
        const containingHighlight = this.findHighlightContainingRange(selectionAnchors);
        const removableHighlightId = highlightId || (containingHighlight ? containingHighlight.id : null);

        // Right-clicking (or tapping) a highlight without selecting anything acts on
        // the whole highlight: the menu behaves as though its text had been selected,
        // so it still offers Copy, Search and the play log rather than removal alone.
        let menuText = selectedText;
        let menuAnchors = selectionAnchors;
        let wholeHighlight = null;
        if (!menuText && removableHighlightId) {
            const hit = this.getHighlightById(removableHighlightId);
            if (hit && hit.text && typeof hit.startItemIdx === 'number') {
                wholeHighlight = hit;
                menuText = hit.text;
                menuAnchors = {
                    startItemIdx: hit.startItemIdx,
                    startOffset: hit.startOffset,
                    endItemIdx: hit.endItemIdx,
                    endOffset: hit.endOffset
                };
            }
        }

        // "Highlight selection" is dropped only when the text the menu is about is
        // itself already highlighted — not merely because some other highlight
        // happened to be under the cursor while text elsewhere was selected.
        const alreadyHighlighted = !!containingHighlight || !!wholeHighlight;

        const parts = [];
        if (menuText) {
            parts.push(`<div class="pdf-context-menu-item" data-action="copy">
                <i class="bi bi-clipboard"></i> Copy
            </div>`);
            parts.push(`<div class="pdf-context-menu-item" data-action="search">
                <i class="bi bi-search"></i> Search Google for "${escapeHtml(this.truncateText(menuText, 40))}"
            </div>`);
            parts.push(`<div class="pdf-context-menu-separator"></div>`);
            if (!alreadyHighlighted) {
                parts.push(`<div class="pdf-context-menu-item" data-action="highlight">
                    <i class="bi bi-highlighter"></i> Highlight selection
                </div>`);
            }
            if (this.onCopyToPlayLog) {
                const playLogLabel = wholeHighlight
                    ? 'Copy highlighted text to play log with location info'
                    : 'Copy selected text to play log with location info';
                parts.push(`<div class="pdf-context-menu-item" data-action="playlog">
                    <i class="bi bi-journal-plus"></i> ${playLogLabel}
                </div>`);
            }
        }
        if (removableHighlightId) {
            if (parts.length > 0) {
                parts.push(`<div class="pdf-context-menu-separator"></div>`);
            }
            parts.push(`<div class="pdf-context-menu-item" data-action="removeHighlight">
                <i class="bi bi-eraser"></i> Remove highlight
            </div>`);
        }
        if (parts.length === 0) return;

        menu.innerHTML = parts.join('');
        menu.classList.toggle('pdf-context-menu-touch', !!options.touchMode);
        menu.style.display = '';

        // When the menu opens from a gesture that is still in flight, that same tap
        // or long-press goes on to produce a click, which the document-level dismiss
        // handler would read as "close it". Give it a grace window to swallow that
        // one click. (The auto selection menu opens from a timer, well after the
        // gesture is over, so it does not ask for this — its first tap is real.)
        if (options.consumeNextClick) {
            this.suppressDismissUntil = Date.now() + PDF_TOUCH_MENU_DISMISS_GRACE_MS;
        }

        // Capture selection context now — clicking the menu item will clear the browser
        // selection. A menu about a whole highlight uses the coordinates frozen on that
        // highlight; otherwise read the live selection, falling back to the cached
        // capture for menus opened after the selection has already gone (the touch paths).
        const selectionPdfY = wholeHighlight
            ? wholeHighlight.pdfY
            : (this.getSelectionPdfY() ?? (this.cachedSelection ? this.cachedSelection.pdfY : null));
        const selectionPdfX = wholeHighlight
            ? wholeHighlight.pdfX
            : (this.getSelectionPdfX() ?? (this.cachedSelection ? this.cachedSelection.pdfX : null));

        // Position relative to the viewer element
        const viewerRect = this.viewerEl.getBoundingClientRect();
        let left = clientX - viewerRect.left;
        let top = clientY - viewerRect.top;

        // Clamp to stay within the viewer bounds
        const menuWidth = menu.offsetWidth;
        const menuHeight = menu.offsetHeight;
        if (left + menuWidth > viewerRect.width) {
            left = viewerRect.width - menuWidth;
        }
        if (top + menuHeight > viewerRect.height) {
            // Anchored menus (touch) flip to the far side of what they belong to
            // rather than sliding up over it; unanchored ones just clamp.
            const aboveTop = options.avoidRect
                ? (options.avoidRect.top - viewerRect.top) - menuHeight - PDF_TOUCH_MENU_OFFSET_PX
                : -1;
            top = aboveTop >= 0 ? aboveTop : viewerRect.height - menuHeight;
        }
        if (left < 0) left = 0;
        if (top < 0) top = 0;

        menu.style.left = `${left}px`;
        menu.style.top = `${top}px`;

        // Handle clicks on menu items. The handler is tracked on the instance and
        // replaced on every show: a menu that is re-shown without being clicked
        // (routine on touch, where selection changes re-anchor it) would otherwise
        // leave earlier listeners armed with stale text and anchors, and a single
        // later click would run all of them.
        if (this.contextMenuClickHandler) {
            menu.removeEventListener('click', this.contextMenuClickHandler);
        }
        this.contextMenuClickHandler = (e) => {
            const item = e.target.closest('.pdf-context-menu-item');
            if (!item) return;

            const action = item.dataset.action;
            if (action === 'copy') {
                navigator.clipboard.writeText(menuText).catch(() => {});
                this.logSelectionToPlayLog(menuText, selectionPdfY, selectionPdfX, 'Copy');
                if (this.stateManager && this.stateManager.getPdfAutoHighlightOnCopy()) {
                    // A no-op when the menu is about an existing highlight: a range
                    // already inside one is never highlighted again.
                    this.addHighlightFromAnchors(menuAnchors, menuText, /*silent*/ true);
                }
            } else if (action === 'search') {
                window.open(`https://www.google.com/search?q=${encodeURIComponent(menuText)}`, '_blank', 'noopener');
            } else if (action === 'playlog') {
                this.logSelectionToPlayLog(menuText, selectionPdfY, selectionPdfX, 'Copy');
                if (this.stateManager && this.stateManager.getPdfAutoHighlightOnCopy()) {
                    this.addHighlightFromAnchors(menuAnchors, menuText, /*silent*/ true);
                }
            } else if (action === 'highlight') {
                this.addHighlightFromAnchors(menuAnchors, menuText);
            } else if (action === 'removeHighlight') {
                this.removeHighlight(removableHighlightId);
            }
            // Whatever was acted on, this capture is spent — don't let the toolbar
            // button re-apply it.
            this.cachedSelection = null;
            this.clearSelectionTimers();
            this.updateHighlightSelectionButton(false);
            this.hideTextContextMenu();
        };
        menu.addEventListener('click', this.contextMenuClickHandler);
    }


    /**
     * Truncates text to a maximum length, adding ellipsis if needed.
     * @param {string} str
     * @param {number} maxLen
     * @returns {string}
     * @private
     */
    truncateText(str, maxLen) {
        if (str.length <= maxLen) return str;
        return str.substring(0, maxLen) + '\u2026';
    }


    /**
     * Hides the custom context menu.
     * @private
     */
    hideTextContextMenu() {
        if (this.selectionMenuTimerId) {
            clearTimeout(this.selectionMenuTimerId);
            this.selectionMenuTimerId = null;
        }
        if (this.contextMenuEl) {
            // Hide only. The click handler is deliberately left attached: the
            // document-level dismiss listener runs in the CAPTURE phase, so clicking
            // a menu item hides the menu before the item's own handler is reached —
            // detaching here would cancel the very action the user just chose. A
            // hidden menu cannot be clicked, so leaving it armed is harmless, and
            // showTextContextMenu replaces it on the next open.
            this.contextMenuEl.style.display = 'none';
        }
    }


    /**
     * Returns the title of the bookmark with the given id.
     * Searches the cached bookmark tree from populateBookmarkSidebar.
     * @param {string} id - The bookmark/named destination ID
     * @returns {string|null} The normalized bookmark title, or null if not found
     */
    lookUpBookmarkTitleFromId(id) {
        if (!id) return null;

        const findInItems = (items) => {
            for (const bookmark of items) {
                if (bookmark.id === id) {
                    return this.normalizeTitleLabel(bookmark.title);
                }
                if (bookmark.children && bookmark.children.length > 0) {
                    const found = findInItems(bookmark.children);
                    if (found) return found;
                }
            }
            return null;
        };

        return findInItems(this.bookmarkCache);
    }


    /**
     * Gets the selected text from the pdf.js text layer, inserting spaces
     * at line boundaries. The text layer renders each text item as a
     * separate span with CSS positioning, so Selection.toString() concatenates
     * text across lines without any whitespace.
     * @returns {string} The selected text with proper spacing
     * @private
     */
    getSelectedTextWithSpaces() {
        const selection = window.getSelection();
        if (!selection || !selection.rangeCount) return '';

        const range = selection.getRangeAt(0);

        // Single text node selection — no line-break issue possible
        if (range.startContainer === range.endContainer) {
            return selection.toString();
        }

        // Walk through text nodes, adding spaces at line boundaries
        const walker = document.createTreeWalker(
            range.commonAncestorContainer,
            NodeFilter.SHOW_TEXT
        );

        const parts = [];
        let lastTop = null;
        let lastLeft = null;
        let node = walker.nextNode();

        while (node) {
            if (!range.intersectsNode(node)) {
                node = walker.nextNode();
                continue;
            }

            let text = node.textContent;

            // Trim to selection boundaries
            const isStart = (node === range.startContainer);
            const isEnd = (node === range.endContainer);
            if (isStart && isEnd) {
                text = text.slice(range.startOffset, range.endOffset);
            } else if (isStart) {
                text = text.slice(range.startOffset);
            } else if (isEnd) {
                text = text.slice(0, range.endOffset);
            }

            if (text) {
                const parentEl = node.parentElement;
                if (parentEl) {
                    const rect = parentEl.getBoundingClientRect();
                    const top = Math.round(rect.top);
                    const left = Math.round(rect.left);
                    if (lastTop !== null && top !== lastTop) {
                        // New line — insert space if boundary lacks whitespace
                        const prev = parts[parts.length - 1] || '';
                        if (prev && !/\s$/.test(prev) && !/^\s/.test(text)) {
                            // Large backward X jump with big Y change = column transition
                            if (lastLeft !== null && left < lastLeft - 100 && Math.abs(top - lastTop) > 200) {
                                parts.push('\n');
                            } else {
                                parts.push(' ');
                            }
                        }
                    }
                    lastTop = top;
                    lastLeft = left;
                }
                parts.push(text);
            }

            if (isEnd) break;
            node = walker.nextNode();
        }

        return parts.join('');
    }


    /**
     * Returns the PDF y-coordinate of the start of the current text selection,
     * read from the data-pdf-y attribute stored on text layer spans.
     * @returns {number|null} The PDF y-coordinate, or null if unavailable
     * @private
     */
    getSelectionPdfY() {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return null;

        // Walk from the anchor node up to find a text layer span with data-pdf-y
        let node = sel.anchorNode;
        while (node && node !== this.textLayerEl) {
            if (node.nodeType === Node.ELEMENT_NODE && node.dataset && node.dataset.pdfY !== undefined) {
                return parseFloat(node.dataset.pdfY);
            }
            node = node.parentNode;
        }
        return null;
    }


    /**
     * Returns the PDF x-coordinate of the start of the current text selection,
     * read from the data-pdf-x attribute stored on text layer spans. Used for
     * column-aware bookmark resolution on multi-column pages.
     * @returns {number|null} The PDF x-coordinate, or null if unavailable
     * @private
     */
    getSelectionPdfX() {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return null;

        let node = sel.anchorNode;
        while (node && node !== this.textLayerEl) {
            if (node.nodeType === Node.ELEMENT_NODE && node.dataset && node.dataset.pdfX !== undefined) {
                return parseFloat(node.dataset.pdfX);
            }
            node = node.parentNode;
        }
        return null;
    }


    /**
     * Extracts the PDF y-coordinate from a bookmark's destination data.
     * @param {Object} bookmark - A bookmark object with a dest array
     * @returns {number|null} The PDF y-coordinate, or null if unavailable
     * @private
     */
    getBookmarkDestY(bookmark) {
        const dest = bookmark.dest;
        if (!dest || !Array.isArray(dest) || dest.length < 2) return null;

        const destType = dest[1]?.name;
        if (destType === 'XYZ' && dest.length >= 4 && dest[3] !== null) return dest[3];
        if ((destType === 'FitH' || destType === 'FitBH') && dest.length >= 3) return dest[2];
        return null;
    }


    /**
     * Analyzes a page's text items to detect a multi-column layout. Returns
     * { multiColumn, columnBoundary } when a vertical column gutter is
     * clearly present, or { multiColumn: false, columnBoundary: null }.
     *
     * Approach: a real multi-column layout has a vertical strip of
     * whitespace running down the page that no text crosses — the column
     * gutter. We bucket the page's X axis, count how many items horizontally
     * overlap each bucket, and look for a low-count bucket in the central
     * area whose count is substantially lower than the peak counts in the
     * regions to its left and right. This is robust to a short column with
     * only a few lines (line-start counts don't matter) and to single-
     * column pages with centered headings (the headings don't create a
     * vertical strip that all other items avoid).
     *
     * @param {Array} items - textContent.items from pdf.js getTextContent()
     * @param {number|null} pageWidth - Page width in PDF user units
     * @returns {{multiColumn: boolean, columnBoundary: number|null}}
     * @private
     */
    _detectMultiColumnLayout(items, pageWidth) {
        const NO_COLUMNS = { multiColumn: false, columnBoundary: null };
        if (!items || items.length < 10 || !pageWidth || pageWidth <= 0) {
            return NO_COLUMNS;
        }

        const NUM_BUCKETS = 80;
        const bucketWidth = pageWidth / NUM_BUCKETS;
        const counts = new Array(NUM_BUCKETS).fill(0);

        let usedItemCount = 0;
        for (const item of items) {
            if (!item.str || !item.transform) continue;
            const left = item.transform[4];
            const w = item.width || 0;
            const right = left + w;
            if (right < 0 || left > pageWidth) continue;
            const bStart = Math.max(0, Math.floor(left / bucketWidth));
            const bEnd = Math.min(NUM_BUCKETS - 1, Math.floor(right / bucketWidth));
            for (let b = bStart; b <= bEnd; b++) counts[b]++;
            usedItemCount++;
        }
        if (usedItemCount < 10) return NO_COLUMNS;

        // Find the lowest-count bucket in the central 50% of the page.
        const centerStart = Math.floor(NUM_BUCKETS * 0.25);
        const centerEnd = Math.floor(NUM_BUCKETS * 0.75);
        let minCount = Infinity;
        let minBucket = -1;
        for (let b = centerStart; b <= centerEnd; b++) {
            if (counts[b] < minCount) {
                minCount = counts[b];
                minBucket = b;
            }
        }
        if (minBucket < 0) return NO_COLUMNS;

        // Compare to peak counts on each side of the candidate gutter.
        let peakLeft = 0;
        for (let b = 0; b < minBucket; b++) {
            if (counts[b] > peakLeft) peakLeft = counts[b];
        }
        let peakRight = 0;
        for (let b = minBucket + 1; b < NUM_BUCKETS; b++) {
            if (counts[b] > peakRight) peakRight = counts[b];
        }

        // Both sides must hold real content (left ≥ 3 lines/items, right ≥ 1
        // so a 1-line right column still counts), and the gutter must be a
        // clear dip — significantly below the WEAKER side's peak.
        if (peakLeft < 3 || peakRight < 1) return NO_COLUMNS;
        const weakerPeak = Math.min(peakLeft, peakRight);
        if (minCount >= weakerPeak * 0.5) return NO_COLUMNS;

        return { multiColumn: true, columnBoundary: (minBucket + 0.5) * bucketWidth };
    }


    /**
     * Extracts the PDF x-coordinate from a bookmark's destination data.
     * @param {Object} bookmark - A bookmark object with a dest array
     * @returns {number|null} The PDF x-coordinate, or null if unavailable
     * @private
     */
    getBookmarkDestX(bookmark) {
        const dest = bookmark.dest;
        if (!dest || !Array.isArray(dest) || dest.length < 2) return null;

        const destType = dest[1]?.name;
        if (destType === 'XYZ' && dest.length >= 3 && dest[2] !== null) return dest[2];
        if ((destType === 'FitV' || destType === 'FitBV' || destType === 'FitR') && dest.length >= 3 && dest[2] !== null) return dest[2];
        return null;
    }


    /**
     * Finds the nearest bookmark before the given position in reading order.
     * Compares page number, PDF y-coordinate (PDF origin is bottom-left, so
     * higher y = higher on page = earlier in reading order), and, when the
     * page is detected as multi-column, the PDF x-coordinate.
     *
     * Two-column reading order is: entire left column top-to-bottom, then
     * entire right column top-to-bottom. So for a right-column selection,
     * any left-column bookmark counts as "prior" regardless of its y, but a
     * same-column bookmark above the selection is preferred (more recent).
     * For a left-column selection, only same-column bookmarks above count.
     *
     * Column detection comes from `columnInfo`, derived from the page's
     * actual rendered text layout (see _detectMultiColumnLayout). That's
     * more reliable than inferring columns from bookmark positions, since
     * a real column might contain no bookmarks at all.
     *
     * @param {number} pageNum - The current page number
     * @param {number|null} pdfY - The PDF y-coordinate of the selection, or null for page-only matching
     * @param {number|null} [pdfX=null] - The PDF x-coordinate of the selection (for column-aware matching)
     * @param {{multiColumn: boolean, columnBoundary: number|null}|null} [columnInfo=null] - Layout info for the selection's page
     * @returns {string|null} The bookmark title, or null if none found
     */
    findNearestPriorBookmarkLabel(pageNum, pdfY, pdfX = null, columnInfo = null) {
        let best = null;
        let bestY = null;
        let bestSameColAsSelection = null;  // tracks the chosen same-page bookmark's column relationship

        const treatPageAsMultiCol = !!(columnInfo && columnInfo.multiColumn
            && columnInfo.columnBoundary != null && pdfX != null);
        const colBoundary = treatPageAsMultiCol ? columnInfo.columnBoundary : null;
        const selectionCol = treatPageAsMultiCol ? (pdfX < colBoundary ? 0 : 1) : null;

        const isBetterMatch = (bookmark, bookmarkY, bookmarkSameColAsSelection) => {
            if (!best) return true;

            // Higher page is closer in reading order (later page but still prior)
            if (bookmark.page !== best.page) {
                return bookmark.page > best.page;
            }
            // Same-page: in multi-column mode, a same-column bookmark always
            // beats a differently-columned one (it's more recent).
            if (treatPageAsMultiCol && bookmarkSameColAsSelection !== bestSameColAsSelection) {
                return bookmarkSameColAsSelection === true;
            }
            // Same page (and same column status if applicable): lower y is closer
            // to the selection in reading order.
            if (bookmarkY !== null && bestY !== null) {
                return bookmarkY < bestY;
            }
            return false;
        };

        const search = (items) => {
            for (const bookmark of items) {
                if (!bookmark.page || bookmark.page > pageNum) {
                    // Skip bookmarks on later pages
                } else if (bookmark.page < pageNum) {
                    // Earlier page: always prior
                    if (isBetterMatch(bookmark, this.getBookmarkDestY(bookmark), null)) {
                        best = bookmark;
                        bestY = this.getBookmarkDestY(bookmark);
                        bestSameColAsSelection = null;
                    }
                } else {
                    // Same page as the selection
                    const bookmarkY = this.getBookmarkDestY(bookmark);

                    let isPrior;
                    let bookmarkSameColAsSelection = null;
                    if (treatPageAsMultiCol) {
                        const bx = this.getBookmarkDestX(bookmark);
                        if (bx === null) {
                            // No X info — fall back to y-only check
                            isPrior = (pdfY === null || bookmarkY === null || bookmarkY >= pdfY);
                        } else {
                            const bookmarkCol = bx < colBoundary ? 0 : 1;
                            if (bookmarkCol > selectionCol) {
                                isPrior = false;                 // later column: not prior
                            } else if (bookmarkCol < selectionCol) {
                                isPrior = true;                  // earlier column: prior at any y
                                bookmarkSameColAsSelection = false;
                            } else {
                                // same column: prior only if at or above selection
                                isPrior = (bookmarkY === null) || (bookmarkY >= pdfY);
                                bookmarkSameColAsSelection = true;
                            }
                        }
                    } else {
                        isPrior = (pdfY === null || bookmarkY === null || bookmarkY >= pdfY);
                    }

                    if (isPrior && isBetterMatch(bookmark, bookmarkY, bookmarkSameColAsSelection)) {
                        best = bookmark;
                        bestY = bookmarkY;
                        bestSameColAsSelection = bookmarkSameColAsSelection;
                    }
                }

                if (bookmark.children && bookmark.children.length > 0) {
                    search(bookmark.children);
                }
            }
        };

        search(this.bookmarkCache);
        return best ? this.normalizeTitleLabel(best.title) : null;
    }


    /**
     * Routes selected/highlighted text to the play-log callback with the
     * current page and the nearest prior bookmark resolved from `pdfY` and
     * `pdfX`. pdfX enables column-aware matching on multi-column pages.
     * Single point of formatting parity between clipboard-copy mirroring,
     * the right-click "Copy to play log" action, and the highlight action.
     * @param {string} text - Plain-text content
     * @param {number|null} pdfY - PDF y-coordinate to anchor bookmark lookup
     * @param {number|null} pdfX - PDF x-coordinate for column-aware matching
     * @param {string} prefix - Short tag for the log entry ("Copy" or "Highlight")
     * @private
     */
    logSelectionToPlayLog(text, pdfY, pdfX, prefix) {
        if (!this.onCopyToPlayLog || !text) return;
        const pageNum = this.currentPage;
        const bookmarkLabel = this.findNearestPriorBookmarkLabel(pageNum, pdfY, pdfX, this.currentPageColumnInfo);
        this.onCopyToPlayLog(text, pageNum, bookmarkLabel, prefix);
    }


    /**
     * Navigates to a PDF destination (internal link target).
     * Handles both named string destinations and explicit destination arrays.
     * @param {string|Array} dest - Named destination string or explicit destination array
     * @private
     */
    async navigateToDestination(dest) {
        if (!this.pdfDoc || !dest) return;

        try {
            if (typeof dest === 'string') {
                // Named destination - delegate to goToBookmark
                await this.goToBookmark(dest);
            } else if (Array.isArray(dest) && dest[0]) {
                // Explicit destination array: [pageRef, type, ...params]
                const pageIndex = await this.pdfDoc.getPageIndex(dest[0]);
                const targetPage = pageIndex + 1;

                const rendered = await this.renderPage(targetPage);
                await this.highlightDestinationIfStillShown(rendered, dest, targetPage);
            }
        } catch (error) {
            console.error('Failed to navigate to destination:', error);
        }
    }


    /**
     * Marks a destination's position (or flashes the whole page) after renderPage drew its page --
     * unless a newer page request overtook that render (rendered is false) or starts while the
     * position is worked out: the mark would then land on whatever page that request shows.
     * @param {boolean} rendered - What renderPage returned for the destination's page
     * @param {Array} dest - The destination array from pdf.js
     * @param {number} pageNum - The destination's page number (1-indexed)
     * @returns {Promise<void>}
     * @private
     */
    async highlightDestinationIfStillShown(rendered, dest, pageNum) {
        if (!rendered) {
            return;
        }
        const renderGeneration = this.renderGeneration;
        const position = await this.extractDestinationPosition(dest, pageNum);
        if (renderGeneration !== this.renderGeneration) {
            return;
        }
        if (position && position.y !== null) {
            this.highlightAtPosition(position);
        } else {
            // Fallback to whole page flash if no position available
            this.highlightPage();
        }
    }


    /**
     * Goes to a specific page.
     * @param {number} pageNum - The page number to navigate to
     * @returns {Promise<boolean>} True if that page is now the one shown; false if it is out of
     *   range, or a newer page request overtook this one (see renderPage)
     */
    async goToPage(pageNum) {
        if (pageNum < 1 || pageNum > this.totalPages) {
            return false;
        }
        // only a page already on screen with no other render pending needs no drawing: going back
        // to the screen's page while a turn away from it is still drawing must win over that turn
        if (pageNum === this.currentPage && this.renderTargetPage === null) {
            return true;
        }
        return await this.renderPage(pageNum);
    }


    /**
     * Returns the currently displayed page number (1-indexed).
     * @returns {number} The current page number
     */
    getCurrentPageNumber() {
        return this.currentPage;
    }


    /**
     * Navigates to a named destination/bookmark in the PDF.
     * @param {string} id - The bookmark/named destination ID
     * @returns {Promise<boolean>} True if the destination was found and navigated to
     */
    async goToBookmark(id, flagFlashPageErrorOnNotFound) {
        if (!this.pdfDoc || !id) {
            return false;
        }

        try {
            // Get the destination array for this named destination
            const dest = await this.pdfDoc.getDestination(id);
            if (!dest) {
                if (flagFlashPageErrorOnNotFound) {
                    this.flashError();
                }
                console.warn(`Bookmark "${id}" not found in PDF`);
                return false;
            }

            // The first element of the destination array is a page reference
            const pageRef = dest[0];

            // Get the page index (0-based) from the page reference
            const pageIndex = await this.pdfDoc.getPageIndex(pageRef);
            const pageNum = pageIndex + 1; // Convert to 1-based

            // Navigate to the page
            const rendered = await this.renderPage(pageNum);

            // Extract position from destination and highlight at that location (not if a newer
            // page request overtook this one; the bookmark was still found, so this returns true)
            await this.highlightDestinationIfStillShown(rendered, dest, pageNum);

            return true;
        } catch (error) {
            console.error(`Failed to navigate to bookmark "${id}":`, error);
            return false;
        }
    }


    /**
     * Extracts the X and Y position from a PDF destination array.
     * Destination format varies: [pageRef, {name: "XYZ"}, left, top, zoom] or similar.
     * @param {Array} dest - The destination array from pdf.js
     * @param {number} pageNum - The page number (1-indexed)
     * @returns {Promise<{x: number|null, y: number|null, pageWidth: number}|null>} Position info or null
     * @private
     */
    async extractDestinationPosition(dest, pageNum) {
        if (!dest || dest.length < 2) {
            return null;
        }

        try {
            const page = await this.pdfDoc.getPage(pageNum);
            const viewport = page.getViewport({ scale: this.scale });

            // dest[1] is the destination type, dest[2] is left, dest[3] is top
            // Common formats:
            // [pageRef, {name: "XYZ"}, left, top, zoom]
            // [pageRef, {name: "FitH"}, top]
            // [pageRef, {name: "FitBH"}, top]
            const destType = dest[1];
            let pdfX = null;
            let pdfY = null;

            if (destType && destType.name === 'XYZ' && dest.length >= 4) {
                // XYZ destination: [pageRef, {name: "XYZ"}, left, top, zoom]
                pdfX = dest[2];
                pdfY = dest[3];
            } else if (destType && (destType.name === 'FitH' || destType.name === 'FitBH') && dest.length >= 3) {
                // FitH/FitBH destination: [pageRef, {name: "FitH"}, top]
                pdfY = dest[2];
            }

            // Convert PDF coordinates to screen coordinates
            const pageWidth = viewport.width;
            const pageHeight = viewport.height / this.scale;

            // PDF Y is from bottom, screen Y is from top
            const screenY = (pdfY !== null && pdfY !== undefined)
                ? (pageHeight - pdfY) * this.scale
                : null;

            // PDF X is from left, same as screen X (just scale it)
            const screenX = (pdfX !== null && pdfX !== undefined)
                ? pdfX * this.scale
                : null;

            return {
                x: screenX,
                y: screenY,
                pageWidth: pageWidth
            };
        } catch (error) {
            console.error('Failed to extract destination position:', error);
            return null;
        }
    }


    /**
     * Retrieves all bookmarks/outline from the PDF.
     * @returns {Promise<Array>} Array of bookmark objects with id, title, page, and children
     */
    async getBookmarkData() {
        if (!this.pdfDoc) {
            return [];
        }

        try {
            const outline = await this.pdfDoc.getOutline();
            if (!outline) {
                return [];
            }

            // Recursively process outline items
            const processItems = async (items, level = 0) => {
                const results = [];
                for (const item of items) {
                    const bookmark = {
                        title: item.title,
                        id: null,
                        page: null,
                        dest: null, // Store raw destination for position extraction
                        level: level,
                        children: []
                    };

                    // Determine the destination ID and page
                    if (typeof item.dest === 'string') {
                        // Named destination - use as ID directly
                        bookmark.id = item.dest;
                        try {
                            const dest = await this.pdfDoc.getDestination(item.dest);
                            bookmark.dest = dest; // Store resolved destination
                            if (dest && dest[0]) {
                                const pageIndex = await this.pdfDoc.getPageIndex(dest[0]);
                                bookmark.page = pageIndex + 1;
                            }
                        } catch (e) {
                            // Could not resolve page
                        }
                    } else if (Array.isArray(item.dest) && item.dest[0]) {
                        // Explicit destination array - first element is page ref
                        bookmark.dest = item.dest; // Store raw destination
                        try {
                            const pageIndex = await this.pdfDoc.getPageIndex(item.dest[0]);
                            bookmark.page = pageIndex + 1;
                            // For explicit destinations, use page number as a fallback ID
                            bookmark.id = `page:${bookmark.page}`;
                        } catch (e) {
                            // Could not resolve page
                        }
                    }

                    // Process children recursively
                    if (item.items && item.items.length > 0) {
                        bookmark.children = await processItems(item.items, level + 1);
                    }

                    results.push(bookmark);
                }
                return results;
            };

            return await processItems(outline);
        } catch (error) {
            console.error('Failed to get bookmark data:', error);
            return [];
        }
    }


    /**
     * The current document's outline, read from pdf.js once per document and then reused.
     * getBookmarkData() walks the whole outline and awaits a destination + page lookup per entry, so
     * every lead lookup (twice per "read lead" when the reader tab is switched to) used to repeat it.
     * Concurrent callers share the one pending read; a new document (or clear()) starts afresh.
     * @returns {Promise<Array>} Array of bookmark objects (see getBookmarkData)
     */
    getCachedBookmarkData() {
        const doc = this.pdfDoc;
        if (!doc) {
            return Promise.resolve([]);
        }
        if (this.bookmarkDataDoc !== doc || !this.bookmarkDataPromise) {
            this.bookmarkDataDoc = doc;
            this.bookmarkDataPromise = this.getBookmarkData();
        }
        return this.bookmarkDataPromise;
    }


    /**
     * Drops the cached outline (called whenever the document changes or is cleared).
     * @private
     */
    invalidateBookmarkCache() {
        this.bookmarkCache = [];
        this.bookmarkDataDoc = null;
        this.bookmarkDataPromise = null;
    }


    /**
     * Toggles the bookmark sidebar visibility.
     */
    toggleBookmarkSidebar() {
        this.isBookmarkSidebarOpen = !this.isBookmarkSidebarOpen;
        this.bookmarkSidebarEl.classList.toggle('open', this.isBookmarkSidebarOpen);
        this.controlsEl.querySelector('.pdf-sidebar-toggle-btn').classList.toggle('active', this.isBookmarkSidebarOpen);
        // should we auto schedule a resize? or maybe the user just wants to navigate and not be annoyed?
        this.scheduleViewerResize();
    }


    /**
     * Opens the bookmark sidebar.
     */
    openBookmarkSidebar() {
        if (!this.isBookmarkSidebarOpen) {
            this.toggleBookmarkSidebar();
        }
    }


    /**
     * Closes the bookmark sidebar.
     */
    closeBookmarkSidebar() {
        if (this.isBookmarkSidebarOpen) {
            this.toggleBookmarkSidebar();
        }
    }


    /**
     * Expands a bookmark section (and its parent sections) in the sidebar by label: the FIRST bookmark,
     * in the sidebar's order, whose title matches the label or any one of a list of labels (a case
     * book's leads section is LEADS_SECTION_LABELS: 'LEADS' and its translations, Oct 30 2026).
     * A title also matches without a trailing " [...]": a debug build adds a heading's id in brackets
     * when its printed title differs from it, so a Swedish booklet's leads bookmark is
     * "LEDTRÅDAR [LEADS]" (lib/casebook/cbrender.calcReportTocText; Oct 30 2026).
     * Does not navigate to the bookmark, just reveals it in the sidebar hierarchy.
     * @param {string|Array<string>} labels - The bookmark title(s) to find and expand
     * @returns {boolean} True if a bookmark was found and expanded
     */
    expandBookmarkByLabel(labels) {
        const wantedTitles = new Set((Array.isArray(labels) ? labels : [labels]).filter(Boolean).map((label) => this.normalizeTitleLabel(label)));
        if (wantedTitles.size === 0) return false;

        const listEl = this.bookmarkSidebarEl.querySelector('.pdf-bookmark-list');
        if (!listEl) return false;

        // Find the bookmark item by searching through all title elements
        const allTitles = listEl.querySelectorAll('.pdf-bookmark-title');
        let targetItem = null;

        for (const titleEl of allTitles) {
            const title = this.normalizeTitleLabel(titleEl.textContent);
            if (wantedTitles.has(title) || wantedTitles.has(title.replace(/\s+\[[^\]]*\]$/, ''))) {
                // Found the matching title, get the parent .pdf-bookmark-item
                targetItem = titleEl.closest('.pdf-bookmark-item');
                break;
            }
        }

        if (!targetItem) {
            return false;
        }

        // Expand this item if it has children
        if (targetItem.classList.contains('has-children')) {
            targetItem.classList.add('expanded');
        }

        // Walk up the DOM tree and expand all parent bookmark items
        let parent = targetItem.parentElement;
        while (parent && parent !== listEl) {
            if (parent.classList.contains('pdf-bookmark-item') && parent.classList.contains('has-children')) {
                parent.classList.add('expanded');
            }
            parent = parent.parentElement;
        }

        // Scroll the item into view within the sidebar?
        if (false) {
            const labelEl = targetItem.querySelector('.pdf-bookmark-label');
            if (labelEl) {
                labelEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }

        return true;
    }


    /**
     * Populates the bookmark sidebar with the PDF's outline/bookmarks.
     * @private
     */
    async populateBookmarkSidebar() {
        const listEl = this.bookmarkSidebarEl.querySelector('.pdf-bookmark-list');
        listEl.innerHTML = '';

        const bookmarks = await this.getCachedBookmarkData();
        this.bookmarkCache = bookmarks;

        if (bookmarks.length === 0) {
            listEl.innerHTML = '<div class="pdf-bookmark-empty">No bookmarks</div>';
            return;
        }

        // Recursively build bookmark tree
        const buildBookmarkItems = (items, parentEl) => {
            for (const bookmark of items) {
                const itemEl = document.createElement('div');
                itemEl.className = 'pdf-bookmark-item';
                if (bookmark.children && bookmark.children.length > 0) {
                    itemEl.classList.add('has-children');
                }

                // Create the bookmark link/label
                const labelEl = document.createElement('div');
                labelEl.className = 'pdf-bookmark-label';
                labelEl.dataset.level = bookmark.level;
                labelEl.style.paddingLeft = `${8 + bookmark.level * 12}px`;

                // Expand/collapse toggle for items with children
                if (bookmark.children && bookmark.children.length > 0) {
                    const toggleEl = document.createElement('span');
                    toggleEl.className = 'pdf-bookmark-toggle';
                    toggleEl.innerHTML = '<i class="bi bi-chevron-right"></i>';
                    toggleEl.addEventListener('click', (e) => {
                        e.stopPropagation();
                        itemEl.classList.toggle('expanded');
                    });
                    labelEl.appendChild(toggleEl);
                }

                // Title text
                const titleEl = document.createElement('span');
                titleEl.className = 'pdf-bookmark-title';
                const bookmarkTitleNormalized = this.normalizeTitleLabel(bookmark.title);
                titleEl.textContent = bookmarkTitleNormalized;
                titleEl.title = bookmarkTitleNormalized; // Tooltip for long titles

                labelEl.appendChild(titleEl);

                // Page number badge
                if (bookmark.page) {
                    const pageEl = document.createElement('span');
                    pageEl.className = 'pdf-bookmark-page';
                    pageEl.textContent = bookmark.page;
                    labelEl.appendChild(pageEl);
                }

                // Click handler to navigate
                labelEl.addEventListener('click', async () => {
                    if (bookmark.id && !bookmark.id.startsWith('page:')) {
                        await this.goToBookmark(bookmark.id);
                    } else if (bookmark.page) {
                        const shown = await this.goToPage(bookmark.page);
                        await this.highlightDestinationIfStillShown(shown, bookmark.dest || null, bookmark.page);
                    }
                    // Notify listener of bookmark click - note that THESE bookmark header links if clicked should go to map
                    // normalize label first?
                    if (this.onBookmarkClick) {
                        const pageNumber = bookmark.page;
                        this.onBookmarkClick(bookmarkTitleNormalized, false, pageNumber);
                    }
                });

                itemEl.appendChild(labelEl);

                // Recursively add children
                if (bookmark.children && bookmark.children.length > 0) {
                    const childrenEl = document.createElement('div');
                    childrenEl.className = 'pdf-bookmark-children';
                    buildBookmarkItems(bookmark.children, childrenEl);
                    itemEl.appendChild(childrenEl);
                }

                parentEl.appendChild(itemEl);
            }
        };

        buildBookmarkItems(bookmarks, listEl);
    }


    // Recursively search for matching bookmark
    findBookmarkWithLeadIdOrLabel(items, leadId) {
            for (const bookmark of items) {
                const bookmarkTitleNormalized = this.normalizeLeadIdTitleLabel(bookmark.title);
                //console.log(`Comparing to "${bookmarkTitleNormalized}".`)
                // normalize/strip title
                if (bookmarkTitleNormalized == leadId) {
                    return bookmark;
                }
                if (bookmark.children && bookmark.children.length > 0) {
                    const found = this.findBookmarkWithLeadIdOrLabel(bookmark.children, leadId);
                    if (found) {
                        return found;
                    }
                }
            }
            return null;
    };



    /**
     * Shows the "Go to Lead" dialog (Ctrl+G), then attempts to navigate to the
     * bookmark matching the typed Lead #. Shows a toast if not found.
     */
    async showLeadGoToDialog() {
        if (!this.pdfDoc) {
            return;
        }
        const result = await PdfLeadGoToDialog.showPdfLeadGoToDialog({
            lastInput: this.lastLeadGoToInput || ''
        });
        if (result.action !== 'go' || !result.leadInput) {
            return;
        }
        this.lastLeadGoToInput = result.leadInput;
        let found = false;
        const pageNum = this.parsePageNumberInput(result.leadInput);
        if (pageNum !== null) {
            if (pageNum >= 1 && pageNum <= this.totalPages) {
                // (no flash if a newer page request overtook this one: it would flash that page)
                if (await this.goToPage(pageNum)) {
                    this.highlightPage();
                }
                found = true;
            } else {
                showStatusWarning(`Page ${pageNum} is out of range (1-${this.totalPages}).`);
            }
        } else {
            found = await this.goToBookmarkByLeadIdOrLabel(result.leadInput, false);
            if (found) {
                // Treat this as a bookmark click so the host app reacts the
                // same way it does for a TOC click: add to history, look up
                // matching POI, etc. flagGoToOnMap=false matches TOC click
                // behavior — navigation stays inside the PDF.
                if (this.onBookmarkClick) {
                    this.onBookmarkClick(result.leadInput, false, this.currentPage);
                }
            } else {
                showStatusWarning(`Lead "${result.leadInput}" not found in PDF.`);
            }
        }
        if (this.onLeadGoToAttempt) {
            this.onLeadGoToAttempt(result.leadInput, found);
        }
    }


    /**
     * Parses an input string as a page number. Accepts a plain integer or
     * the form "P#" / "p#" (e.g., "P42").
     * @param {string} input
     * @returns {number|null} The parsed page number, or null if not a page reference.
     */
    parsePageNumberInput(input) {
        const match = (input || '').trim().match(/^[Pp]?(\d+)$/);
        if (!match) {
            return null;
        }
        const n = parseInt(match[1], 10);
        return Number.isFinite(n) ? n : null;
    }


    /**
     * Finds and navigates to a bookmark by its label/title.
     * Searches recursively through all bookmarks including nested children.
     * @param {string} label - The bookmark title to search for (case-sensitive)
     * @returns {Promise<boolean>} True if the bookmark was found and navigated to
     */
    async goToBookmarkByLeadIdOrLabel(label, flagFlashPageErrorOnNotFound) {
        if (!this.pdfDoc || !label) {
            return false;
        }

        const bookmarks = await this.getCachedBookmarkData();
        if (bookmarks.length === 0) {
            return false;
        }

        // normalize 
        let leadId = this.normalizeLeadIdTitleLabel(label);
        //console.log(`ATTN: Searching for lead bookmark ${leadId}.`)

        // find it
        const bookmark = this.findBookmarkWithLeadIdOrLabel(bookmarks, leadId);
        if (!bookmark) {
            //console.warn(`Bookmark with leadId "${leadId}" not found in PDF`);
            if (flagFlashPageErrorOnNotFound) {
                this.flashError();
            }
            return false;
        }

        // Navigate using the bookmark's id or page
        if (bookmark.id && !bookmark.id.startsWith('page:')) {
            // Named destination - use goToBookmark (which includes highlight)
            return await this.goToBookmark(bookmark.id, flagFlashPageErrorOnNotFound);
        } else if (bookmark.page) {
            // Explicit destination - use goToPage
            const shown = await this.goToPage(bookmark.page);

            // Try to highlight at specific position if destination info available (whole page
            // otherwise; nothing if a newer page request overtook this one)
            await this.highlightDestinationIfStillShown(shown, bookmark.dest || null, bookmark.page);
            return true;
        }

        return false;
    }


    /**
     * Normalizes a title/label for case-insensitive comparison.
     * @param {string} text - The text to normalize
     * @returns {string} Lowercase text with leading/trailing spaces removed
     * @private
     */
    normalizeTitleLabel(text) {
        return (text || '').toLowerCase().trim();
    }


    /**
     * Normalizes a lead id or if not a lead id falls back to title/label for case-insensitive comparison.
     * @param {string} text - The text to normalize
     * @returns {string} Lowercase text with leading/trailing spaces removed
     * @private
     */
    normalizeLeadIdTitleLabel(text) {
        let leadId = getDefaultLeadIdPatterner().normalizeLeadId(text)
        if (leadId != null) {
            return leadId
        }
        return (text || '').toLowerCase().trim();
    }


    /**
     * Called when the user clicks on a hyperlinked bookmark title.
     * Invokes the onBookmarkClick callback with the normalized label.
     * @param {string} titleLabel - The bookmark title text
     * @param {boolean} flagGoToOnMap - whether this click should jump to map location
     */
    userClicksOnHyperlinkedBookmarkTitle(titleLabel, pageNumber) {
        const bookmarkTitleNormalized = this.normalizeTitleLabel(titleLabel);
        if (this.onBookmarkClick) {
            this.onBookmarkClick(bookmarkTitleNormalized, true, pageNumber);
        }
    }


    /**
     * Triggers a visual highlight flash on the PDF canvas to draw attention.
     * Used as fallback when no specific position is available.
     * @private
     */
    highlightPage() {
        if (!this.canvasEl) return;

        // Remove class first in case animation is already running
        this.canvasEl.classList.remove('highlight-flash');

        // Force reflow to restart animation
        void this.canvasEl.offsetWidth;

        // Add the animation class
        this.canvasEl.classList.add('highlight-flash');

        // Remove class after animation completes
        setTimeout(() => {
            this.canvasEl.classList.remove('highlight-flash');
        }, 600);
    }


    /**
     * Triggers a red error flash on the PDF canvas to indicate an error (e.g., bookmark not found).
     */
    flashError() {
        if (!this.canvasEl) return;

        // Remove class first in case animation is already running
        this.canvasEl.classList.remove('error-flash');

        // Force reflow to restart animation
        void this.canvasEl.offsetWidth;

        // Add the animation class
        this.canvasEl.classList.add('error-flash');

        // Remove class after animation completes (1.2s animation)
        setTimeout(() => {
            this.canvasEl.classList.remove('error-flash');
        }, 1200);
    }


    /**
     * Highlights a specific position on the PDF canvas with a horizontal bar.
     * If X position is available, highlights only the relevant column/portion.
     * Also scrolls the canvas container to show the highlighted position.
     * @param {{x: number|null, y: number, pageWidth: number}} position - Position info from extractDestinationPosition
     * @private
     */
    highlightAtPosition(position) {
        if (!this.canvasContainer || !this.canvasEl || !position) return;

        const { x, y, pageWidth } = position;

        // Remove any existing highlight bar
        const existingBar = this.canvasContainer.querySelector('.pdf-highlight-bar');
        if (existingBar) {
            existingBar.remove();
        }

        // Create highlight bar element
        const highlightBar = document.createElement('div');
        highlightBar.className = 'pdf-highlight-bar';

        // Position the bar relative to the canvas
        // The canvas is centered in the container, so we need to calculate the offset
        const canvasRect = this.canvasEl.getBoundingClientRect();
        const containerRect = this.canvasContainer.getBoundingClientRect();
        const canvasOffsetLeft = canvasRect.left - containerRect.left + this.canvasContainer.scrollLeft;
        const canvasOffsetTop = canvasRect.top - containerRect.top + this.canvasContainer.scrollTop;

        // Determine highlight bar width and X position based on destination X
        let barLeft = canvasOffsetLeft;
        let barWidth = canvasRect.width;

        if (x !== null && pageWidth > 0) {
            // X position is available - determine which column/portion
            const xRatio = x / pageWidth;

            if (xRatio < 0.4) {
                // Left column (roughly left 50% of page)
                barWidth = canvasRect.width * 0.52;
                barLeft = canvasOffsetLeft;
            } else if (xRatio > 0.45) {
                // Right column (roughly right 50% of page)
                barWidth = canvasRect.width * 0.52;
                barLeft = canvasOffsetLeft + (canvasRect.width * 0.48);
            }
            // If xRatio is between 0.4-0.45, it's ambiguous - use full width
        }

        // Position the highlight bar
        highlightBar.style.top = `${canvasOffsetTop + y - 12}px`; // Center the 24px bar on the position
        highlightBar.style.left = `${barLeft}px`;
        highlightBar.style.width = `${barWidth}px`;

        // Add to container
        this.canvasContainer.appendChild(highlightBar);

        // Scroll to show the highlighted position
        const scrollTarget = canvasOffsetTop + y - (this.canvasContainer.clientHeight / 3);
        this.canvasContainer.scrollTo({
            top: Math.max(0, scrollTarget),
            behavior: 'smooth'
        });

        // Remove the highlight bar after animation completes
        setTimeout(() => {
            highlightBar.remove();
        }, 2000);
    }


    // ==========================================
    // SEARCH FUNCTIONALITY
    // ==========================================

    /**
     * Extracts text content from a PDF page with position information.
     * Results are cached to avoid re-extraction.
     * @param {number} pageNum - The page number (1-indexed)
     * @returns {Promise<Array>} Array of text items with position info
     * @private
     */
    async extractPageText(pageNum) {
        // Check cache first
        if (this.pageTextCache.has(pageNum)) {
            return this.pageTextCache.get(pageNum);
        }

        const pdfDoc = this.pdfDoc;
        const page = await pdfDoc.getPage(pageNum);
        const textContent = await page.getTextContent();

        // textContent.items contains {str, transform, width, height} objects
        // transform[4] = x position, transform[5] = y position
        const textItems = textContent.items.map(item => ({
            text: item.str,
            x: item.transform[4],
            y: item.transform[5],
            width: item.width,
            height: item.height || 12 // Default height if not provided
        }));

        // (not when another document was loaded meanwhile: the cache now belongs to that one)
        if (pdfDoc === this.pdfDoc) {
            this.pageTextCache.set(pageNum, textItems);
        }
        return textItems;
    }


    /**
     * Performs a text search across all pages of the PDF.
     * @param {string} query - The search query
     */
    async performSearch(query) {
        const trimmedQuery = (query || '').trim();

        if (!trimmedQuery || !this.pdfDoc) {
            this.clearSearch();
            return;
        }

        // Skip if query hasn't changed
        if (trimmedQuery.toLowerCase() === this.searchQuery) {
            return;
        }

        // Each search awaits every page's text, and a newer search (or a clearSearch, which every new
        // document load does) can start meanwhile: an overtaken search stops at its next page, and the
        // results are built in a LOCAL list assigned once at the end -- the loop used to read
        // this.searchQuery and push into this.searchResults on every pass, so it went on searching for
        // the NEW query into the new list (every match from there on counted twice), and it called
        // getPage on a document that had been closed
        const generation = ++this.searchGeneration;
        const isSuperseded = () => (generation !== this.searchGeneration);
        const pdfDoc = this.pdfDoc;
        const lowerQuery = trimmedQuery.toLowerCase();
        const results = [];

        this.searchQuery = lowerQuery;
        this.searchResults = [];
        this.currentMatchIndex = -1;

        // Search all pages
        for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
            let textItems;
            try {
                textItems = await this.extractPageText(pageNum);
            } catch (error) {
                if (isSuperseded() || (pdfDoc !== this.pdfDoc)) {
                    // (the document was closed or replaced under this search)
                    return;
                }
                console.error(`ERROR: PDF search could not read the text of page ${pageNum}:`, error);
                continue;
            }
            if (isSuperseded() || (pdfDoc !== this.pdfDoc)) {
                return;
            }

            // Build full page text while tracking character positions
            let fullText = '';
            const charToItem = []; // Maps character index to textItem index

            for (let i = 0; i < textItems.length; i++) {
                const startPos = fullText.length;
                fullText += textItems[i].text;
                // Map each character to this text item
                for (let j = startPos; j < fullText.length; j++) {
                    charToItem.push(i);
                }
            }

            // Find all matches on this page
            const lowerText = fullText.toLowerCase();
            let searchIndex = 0;
            let matchPos;

            while ((matchPos = lowerText.indexOf(lowerQuery, searchIndex)) !== -1) {
                // Find the text item containing this match
                const itemIndex = charToItem[matchPos];
                const textItem = textItems[itemIndex];

                results.push({
                    pageNum,
                    textIndex: matchPos,
                    text: fullText.substring(matchPos, matchPos + lowerQuery.length),
                    x: textItem ? textItem.x : 0,
                    y: textItem ? textItem.y : 0,
                    width: textItem ? textItem.width : 100,
                    height: textItem ? textItem.height : 12
                });

                searchIndex = matchPos + 1;
            }
        }

        this.searchResults = results;
        this.updateSearchUI();

        // Navigate to first match if found
        if (this.searchResults.length > 0) {
            await this.goToMatch(0);
        }
    }


    /**
     * Clears the current search state and UI.
     */
    clearSearch() {
        // (stops a search still reading page text: see performSearch)
        this.searchGeneration++;
        this.searchQuery = '';
        this.searchResults = [];
        this.currentMatchIndex = -1;
        this.searchInputEl.value = '';
        this.updateSearchUI();
        this.removeSearchHighlight();
    }


    /**
     * Updates the search UI (match info display and clear button visibility).
     * @private
     */
    updateSearchUI() {
        // Update match info
        if (this.searchResults.length > 0) {
            const current = this.currentMatchIndex + 1;
            this.matchInfoEl.textContent = `${current} of ${this.searchResults.length}`;
            this.matchInfoEl.classList.remove('no-matches');
        } else if (this.searchQuery) {
            this.matchInfoEl.textContent = 'No matches';
            this.matchInfoEl.classList.add('no-matches');
        } else {
            this.matchInfoEl.textContent = '';
            this.matchInfoEl.classList.remove('no-matches');
        }

        // Update clear button visibility
        if (this.searchInputEl.value) {
            this.searchClearBtnEl.classList.add('visible');
        } else {
            this.searchClearBtnEl.classList.remove('visible');
        }
    }


    /**
     * Navigates to a specific match by index.
     * @param {number} matchIndex - The index of the match to navigate to
     */
    async goToMatch(matchIndex) {
        if (matchIndex < 0 || matchIndex >= this.searchResults.length) {
            return;
        }

        this.currentMatchIndex = matchIndex;
        const match = this.searchResults[matchIndex];

        // Navigate to page if different (or still being drawn). If a newer page request overtook
        // this one, the match is not on the page shown, so it is not marked there
        if (await this.goToPage(match.pageNum)) {
            // Highlight the match
            this.highlightSearchMatch(match);
        }
        this.updateSearchUI();
    }


    /**
     * Navigates to the next search match (wraps around). With an empty search bar
     * there is nothing to step through, so the control does the next most useful
     * thing and steps to the next highlight/note instead.
     */
    async nextMatch() {
        if (!this.hasSearchText()) {
            await this.nextHighlight();
            return;
        }
        if (this.searchResults.length === 0) return;
        const next = (this.currentMatchIndex + 1) % this.searchResults.length;
        await this.goToMatch(next);
    }


    /**
     * Navigates to the previous search match (wraps around), or to the previous
     * highlight/note when the search bar is empty (see nextMatch).
     */
    async previousMatch() {
        if (!this.hasSearchText()) {
            await this.previousHighlight();
            return;
        }
        if (this.searchResults.length === 0) return;
        const prev = (this.currentMatchIndex - 1 + this.searchResults.length) % this.searchResults.length;
        await this.goToMatch(prev);
    }


    /**
     * Highlights a search match on the PDF canvas.
     * @param {Object} match - The match object with position info
     * @private
     */
    highlightSearchMatch(match) {
        if (!this.canvasContainer || !this.canvasEl || !match) return;

        // Remove any existing search highlight
        this.removeSearchHighlight();

        // Get canvas position info
        const canvasRect = this.canvasEl.getBoundingClientRect();
        const containerRect = this.canvasContainer.getBoundingClientRect();
        const canvasOffsetLeft = canvasRect.left - containerRect.left + this.canvasContainer.scrollLeft;
        const canvasOffsetTop = canvasRect.top - containerRect.top + this.canvasContainer.scrollTop;

        // Calculate the position on the rendered canvas
        // PDF coordinates: origin at bottom-left, y increases upward
        // Canvas coordinates: origin at top-left, y increases downward
        const canvasHeight = parseFloat(this.canvasEl.style.height);

        // Convert PDF y coordinate to canvas y coordinate
        const screenY = canvasHeight - (match.y * this.scale);
        const screenX = match.x * this.scale;

        // Estimate width based on text length and average character width
        const estimatedWidth = Math.max(match.width * this.scale, this.searchQuery.length * 8 * this.scale);
        const highlightHeight = match.height * this.scale;

        // Create highlight element
        const highlightEl = document.createElement('div');
        highlightEl.className = 'pdf-search-highlight';
        highlightEl.style.left = `${canvasOffsetLeft + screenX}px`;
        highlightEl.style.top = `${canvasOffsetTop + screenY - highlightHeight}px`;
        highlightEl.style.width = `${estimatedWidth}px`;
        highlightEl.style.height = `${highlightHeight + 4}px`;

        this.canvasContainer.appendChild(highlightEl);

        // Scroll to show the highlight
        const scrollTarget = canvasOffsetTop + screenY - highlightHeight - (this.canvasContainer.clientHeight / 3);
        this.canvasContainer.scrollTo({
            top: Math.max(0, scrollTarget),
            behavior: 'smooth'
        });
    }


    /**
     * Removes the current search highlight from the canvas container.
     * @private
     */
    removeSearchHighlight() {
        const existingHighlight = this.canvasContainer?.querySelector('.pdf-search-highlight');
        if (existingHighlight) {
            existingHighlight.remove();
        }
    }


    /**
     * Focuses the search input field.
     */
    focusSearchInput() {
        if (this.searchInputEl) {
            this.searchInputEl.focus();
            this.searchInputEl.select();
        }
    }


    /**
     * Gives the viewer the keyboard focus, so its keys work at once (MapApp calls this when the
     * reader is brought up for the user: the Case Book tab clicked, a lead opened from the sidebar).
     */
    focusViewer() {
        if (this.viewerEl) {
            this.viewerEl.focus({ preventScroll: true });
        }
    }


    /**
     * Handles a key pressed while the focus is OUTSIDE the viewer, which MapApp passes on when
     * nothing else wants it and the reader tab is the one on screen (see
     * MapApp.routeUnfocusedKeyToPdfReader). Acts only while a pdf is loaded and the viewer takes up
     * room on screen; a key pressed inside the viewer is its own listener's.
     * @param {KeyboardEvent} e
     */
    handleKeyFromOutside(e) {
        if (!this.pdfDoc || !this.viewerEl || this.viewerEl.contains(e.target)) return;
        // (zero size: its tab is hidden, or the full-width sidebar has squeezed the main panel shut)
        if (this.viewerEl.offsetWidth === 0 || this.viewerEl.offsetHeight === 0) return;
        this.handleReaderKey(e);
    }


    /**
     * Goes to the previous page.
     * @param {Object} [options]
     * @param {boolean} [options.atBottom=false] - Show the page's bottom rather than its top (the Up
     *   key at the top edge: reading on upwards continues where the previous page ends)
     */
    async previousPage({ atBottom = false } = {}) {
        // (from the page still being drawn, if any: two quick presses go back two pages)
        const fromPage = this.getRenderTargetPage();
        if (fromPage > 1) {
            const shown = await this.goToPage(fromPage - 1);
            // (goToPage resolves once the page is drawn, so the page area has its new height)
            this.canvasContainer.scrollTop = (atBottom && shown) ? this.canvasContainer.scrollHeight : 0;
        }
    }


    /**
     * Goes to the next page.
     */
    async nextPage() {
        // (from the page still being drawn, if any: two quick presses go on two pages)
        const fromPage = this.getRenderTargetPage();
        if (fromPage < this.totalPages) {
            await this.goToPage(fromPage + 1);
            this.canvasContainer.scrollTop = 0;
        }
    }


    /**
     * Zooms in by 25%.
     */
    async zoomIn() {
        this.lastZoomMode = 'manual';
        this.canvasContainer.style.overflowX = '';
        this.scale = Math.min(this.scale + 0.25, 4.0);
        this.updateZoomInfo();
        await this.renderPage(this.getRenderTargetPage());
    }


    /**
     * Zooms out by 25%.
     */
    async zoomOut() {
        this.lastZoomMode = 'manual';
        this.canvasContainer.style.overflowX = '';
        this.scale = Math.max(this.scale - 0.25, 0.25);
        this.updateZoomInfo();
        await this.renderPage(this.getRenderTargetPage());
    }


    /**
     * Sets a specific zoom level.
     * @param {number} scale - The zoom scale (1.0 = 100%)
     */
    async setZoom(scale) {
        this.lastZoomMode = 'manual';
        this.canvasContainer.style.overflowX = '';
        this.scale = Math.max(0.25, Math.min(scale, 4.0));
        this.updateZoomInfo();
        await this.renderPage(this.getRenderTargetPage());
    }


    /**
     * Fits the PDF to the width of the container.
     */
    async fitToWidth() {
        if (!this.pdfDoc) return;

        this.lastZoomMode = 'fitWidth';
        try {
            // (the page a pending page change is drawing, if any, read again after the await: a
            // refit redraws the page being turned to, never the one being turned away from)
            const page = await this.pdfDoc.getPage(this.getRenderTargetPage());
            const viewport = page.getViewport({ scale: 1.0 });
            const fitFactor = this.getFitSizeZoomFactor();
            // 40px = 20px left + 20px right base padding on the canvas container.
            const containerWidth = this.canvasContainer.clientWidth - 40;
            this.scale = (containerWidth / viewport.width) * fitFactor;
            // Hide horizontal scrollbar in fit-width mode; we manually center
            // the scroll position below so cropping is equal on both sides.
            this.canvasContainer.style.overflowX = 'hidden';
            this.updateZoomInfo();
            await this.renderPage(this.getRenderTargetPage());
            // With safe-center, an overflowing page is left-aligned. Manually
            // scroll halfway so the visible area is centered on the page.
            const overflowX = this.canvasContainer.scrollWidth - this.canvasContainer.clientWidth;
            if (overflowX > 0) {
                this.canvasContainer.scrollLeft = overflowX / 2;
            }
        } catch (error) {
            console.error('Failed to fit to width:', error);
        }
    }


    /**
     * Adjusts the PDF display when the viewer container is resized.
     * Called automatically via ResizeObserver with debouncing.
     * Respects the user's last zoom mode choice.
     */
    async adjustToViewerSizeChanges() {
        if (!this.pdfDoc) return;

        if (this.lastZoomMode === 'fitPage') {
            await this.fitToPage();
        } else if (this.lastZoomMode === 'fitWidth') {
            await this.fitToWidth();
        }
        // If lastZoomMode is 'manual' or null, do nothing
    }


    /**
     * Fits the entire PDF page within the visible container area.
     */
    async fitToPage() {
        if (!this.pdfDoc) return;

        this.lastZoomMode = 'fitPage';
        // Restore default horizontal overflow (was hidden by fitToWidth).
        this.canvasContainer.style.overflowX = '';
        try {
            // (the page a pending page change is drawing, if any, read again after the await: a
            // refit redraws the page being turned to, never the one being turned away from)
            const page = await this.pdfDoc.getPage(this.getRenderTargetPage());
            const viewport = page.getViewport({ scale: 1.0 });

            // Get container dimensions minus padding (20px each side = 40px) plus
            // 2px buffer to prevent scrollbar from subpixel rounding.
            const containerWidth = this.canvasContainer.clientWidth - 42;
            const containerHeight = this.canvasContainer.clientHeight - 42;

            // Calculate scale to fit both width and height. Fit-page does not
            // apply the extra fit-size zoom (only fit-width does).
            const scaleX = containerWidth / viewport.width;
            const scaleY = containerHeight / viewport.height;
            this.scale = Math.min(scaleX, scaleY);

            this.updateZoomInfo();
            await this.renderPage(this.getRenderTargetPage());
        } catch (error) {
            console.error('Failed to fit to page:', error);
        }
    }


    /**
     * Updates the zoom info display.
     * @private
     */
    updateZoomInfo() {
        this.zoomInfoEl.textContent = `${Math.round(this.scale * 100)}%`;
    }


    /**
     * Shows or hides the loading indicator.
     * @param {boolean} show - Whether to show the loading indicator
     * @private
     */
    showLoading(show) {
        this.loadingEl.style.display = show ? 'flex' : 'none';
    }


    /**
     * Shows an error message.
     * @param {string} message - The error message to display
     * @private
     */
    showError(message) {
        this.errorEl.textContent = message;
        this.errorEl.style.display = 'block';
    }


    /**
     * Hides the error message.
     * @private
     */
    hideError() {
        this.errorEl.style.display = 'none';
    }


    /**
     * Shows a warning toast message that flashes and fades out.
     * @param {string} message - The warning message to display
     */
    showWarningToast(message) {
        this.warningToastEl.textContent = message;
        // Remove and re-add class to restart animation
        this.warningToastEl.classList.remove('show');
        // Force reflow to restart animation
        void this.warningToastEl.offsetWidth;
        this.warningToastEl.classList.add('show');

        // Remove class after animation duration to prevent replay when viewer is re-shown.
        // Must use setTimeout (not animationend) because animationend won't fire if
        // the element is hidden mid-animation.
        setTimeout(() => {
            this.warningToastEl.classList.remove('show');
        }, 2500);
    }


    /**
     * Clears the current PDF and resets the viewer.
     */
    async clear() {
        await this.abandonPendingRenders();
        if (this.pdfDoc) {
            await this.pdfDoc.loadingTask.destroy();
            this.pdfDoc = null;
        }
        this.currentUrl = null;
        this.currentFileName = null;
        this.currentPage = 1;
        this.totalPages = 0;
        this.scale = 1.0;

        // Clear search state, text cache and outline cache
        this.clearSearch();
        this.pageTextCache.clear();
        this.invalidateBookmarkCache();

        // Clear in-memory highlight state (store on disk is untouched)
        this.pageHighlights.clear();
        this.highlightsByDocUrl = null;
        this.currentHighlightNavId = null;
        this.updatePageHighlightIndicator();

        // Clear the saved last PDF URL in state manager
        if (this.stateManager) {
            this.stateManager.setLastPdfUrl('');
        }

        // Clear canvas and annotation links
        const context = this.canvasEl.getContext('2d');
        context.clearRect(0, 0, this.canvasEl.width, this.canvasEl.height);
        this.canvasEl.width = 0;
        this.canvasEl.height = 0;
        this.canvasWrapperEl.style.display = 'none';
        this.annotationLayerEl.innerHTML = '';
        this.textLayerEl.innerHTML = '';

        // Reset UI
        this.pageInputEl.value = 1;
        this.totalPagesEl.textContent = '0';
        this.updateZoomInfo();
        this.updateCurrentUrlDisplay(null);
        this.hideError();
        this.placeholderEl.style.display = '';
    }


    /**
     * Destroys the viewer and cleans up resources.
     */
    async destroy() {
        // Clean up resize observer
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        if (this.resizeTimerId) {
            clearTimeout(this.resizeTimerId);
            this.resizeTimerId = null;
        }

        // Clean up search input handler
        if (this.searchCleanup) {
            this.searchCleanup();
            this.searchCleanup = null;
        }

        // Selection tracking and the context-menu dismiss click are the two listeners living
        // on the document, so they outlive the viewer's own DOM unless taken down explicitly.
        if (this.selectionChangeHandler) {
            document.removeEventListener('selectionchange', this.selectionChangeHandler);
            this.selectionChangeHandler = null;
        }
        if (this.documentDismissClickHandler) {
            document.removeEventListener('click', this.documentDismissClickHandler, true);
            this.documentDismissClickHandler = null;
        }
        this.clearSelectionTimers();
        this.cachedSelection = null;

        await this.clear();
        this.container.innerHTML = '';
        this.viewerEl = null;
        this.canvasEl = null;
        this.textLayerEl = null;
        this.controlsEl = null;
    }


    /**
     * Returns whether the viewer has a PDF loaded.
     * @returns {boolean}
     */
    hasPdf() {
        return this.pdfDoc !== null;
    }


    /**
     * Returns the current PDF URL.
     * @returns {string|null}
     */
    getCurrentUrl() {
        return this.currentUrl;
    }


    /**
     * Returns the display name of the currently loaded PDF (filename for URL
     * loads, the supplied display name for local-file loads), or null if none.
     * @returns {string|null}
     */
    getCurrentFileName() {
        return this.currentFileName;
    }


    /**
     * Collects all bookmark labels from the loaded PDF and returns them as a newline-separated string.
     * Recursively traverses the bookmark tree to include all levels.
     * @returns {Promise<string>} Newline-separated bookmark titles, or empty string if no PDF/bookmarks
     */
    async calcPdfLeads() {
        const bookmarks = await this.getCachedBookmarkData();
        if (bookmarks.length === 0) {
            return '';
        }

        const labels = [];
        const collectLabels = (items) => {
            for (const bookmark of items) {
                const bookmarkTitleNormalized = this.normalizeTitleLabel(bookmark.title);
                if (bookmarkTitleNormalized) {
                    labels.push(bookmarkTitleNormalized);
                }
                if (bookmark.children && bookmark.children.length > 0) {
                    collectLabels(bookmark.children);
                }
            }
        };

        collectLabels(bookmarks);
        return labels.join('\n');
    }




    // ==========================================
    // MEDIA URL POPUP
    // ==========================================

    /**
     * Returns true if the URL points to a media file (image/audio/video) by extension.
     * @param {string} url
     * @returns {boolean}
     */
    static isMediaUrl(url) {
        return PdfViewer.MEDIA_EXTENSIONS.has(urlExtension(url));
    }


    /**
     * Returns true if the url points to an .m3u/.m3u8 playlist file (by extension, ignoring query/hash).
     * @param {string} url
     * @returns {boolean}
     */
    static isPlaylistUrl(url) {
        return PdfViewer.MEDIA_PLAYLIST_EXTS.has(urlExtension(url));
    }


    /**
     * Returns true if the url points to an audio file (by extension, ignoring query/hash).
     * @param {string} url
     * @returns {boolean}
     */
    static isAudioUrl(url) {
        return PdfViewer.MEDIA_AUDIO_EXTS.has(urlExtension(url));
    }


    /**
     * If the url is a /cases/mviewer/ media-viewer page link (?pg= or ?path=), returns the raw
     * media file url it wraps; otherwise null. Inverse of the server-side
     * convertMediaFileUrlToViewerUrl (lib/casebook/cbfuncs_core_support.py): ?pg=CASEDIR/FILE maps
     * to the /f/ uploadsStory alias, ?path=X maps to /media/X. The url's own origin is preserved,
     * and a backgroundLoop marker is re-appended so hasBackgroundLoop/stripBackgroundLoop keep
     * working on the resolved url.
     * @param {string} url
     * @returns {string|null}
     */
    static resolveMviewerFileUrl(url) {
        if (!url || typeof url !== 'string') return null;
        let u;
        try {
            u = new URL(url, window.location.href);
        } catch (e) {
            return null;
        }
        if (!/\/cases\/mviewer\/?$/.test(u.pathname)) return null;
        const pg = u.searchParams.get('pg');
        const path = u.searchParams.get('path');
        // searchParams hands back the DECODED file path, and it goes back into a url path here:
        // re-encode what would read as url structure there (# a fragment, ? a query, % an escape).
        // The pdf's link carries such names encoded since Oct 2026 (jrfilefinder.findUrlForFullPath).
        // Spaces and the rest stay as before, so a track url already stored in the User Playlist
        // still matches the one this returns.
        const asUrlPath = (value) => value.replace(/^\/+/, '').replace(/[%#?]/g, encodeURIComponent);
        let rawPath = null;
        if (pg) {
            rawPath = '/f/' + asUrlPath(pg);
        } else if (path) {
            rawPath = '/media/' + asUrlPath(path);
        }
        if (!rawPath) return null;
        let raw = u.origin + rawPath;
        if (u.searchParams.has('backgroundLoop')) {
            raw += '?backgroundLoop';
        }
        return raw;
    }


    /**
     * Opens the given media URL in a Bootstrap modal popup.
     * Renders an <img>, <video>, or <audio> element based on file extension.
     * @param {string} url
     */
    showMediaInPopup(url) {
        const ext = urlExtension(url);
        const escapedUrl = escapeHtml(url);

        // short, readable title (the full url stays as a tooltip + on the "open in new tab" link); a
        // long url in the header would otherwise force the popup wider than the image needs to be
        const escapedTitle = escapeHtml(this.extractFilename(url));

        let mediaHtml;
        if (PdfViewer.MEDIA_VIDEO_EXTS.has(ext)) {
            mediaHtml = `<video class="pdf-media-popup-video" src="${escapedUrl}" controls autoplay></video>`;
        } else if (PdfViewer.MEDIA_AUDIO_EXTS.has(ext)) {
            mediaHtml = `<audio class="pdf-media-popup-audio" src="${escapedUrl}" controls autoplay></audio>`;
        } else {
            mediaHtml = `<img class="pdf-media-popup-image" src="${escapedUrl}" alt="">`;
        }

        const modalEl = document.createElement('div');
        modalEl.className = 'modal fade pdf-media-popup-modal';
        modalEl.tabIndex = -1;
        // pdf-media-popup-fit makes the dialog shrink to the media's size (scaled down to fit the
        // viewport via CSS) instead of using a fixed wide modal size
        modalEl.innerHTML = `
            <div class="modal-dialog modal-dialog-centered pdf-media-popup-fit">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title text-truncate" title="${escapedUrl}">${escapedTitle}</h5>
                        <a href="${escapedUrl}" target="_blank" rel="noopener noreferrer" class="btn btn-sm btn-outline-secondary me-2" title="Open in new tab">
                            <i class="bi bi-box-arrow-up-right"></i>
                        </a>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body pdf-media-popup-body">
                        ${mediaHtml}
                    </div>
                </div>
            </div>
        `;
        document.body.appendChild(modalEl);
        const modal = new bootstrap.Modal(modalEl);
        modalEl.addEventListener('hidden.bs.modal', () => {
            try { modal.dispose(); } catch (e) { /* ignore */ }
            modalEl.remove();
        });
        modal.show();
    }


    /**
     * Returns true if the url carries the ?backgroundLoop marker added by the $qrCode backgroundLoop option.
     * @param {string} url
     * @returns {boolean}
     */
    static hasBackgroundLoop(url) {
        if (!url || typeof url !== 'string') return false;
        return /[?&]backgroundLoop\b/i.test(url);
    }


    /**
     * Strips the backgroundLoop marker query param, returning the real media file url.
     * @param {string} url
     * @returns {string}
     */
    static stripBackgroundLoop(url) {
        if (!url || typeof url !== 'string') return url;
        return url
            .replace(/([?&])backgroundLoop(=[^&]*)?(&|$)/i, '$1')
            .replace(/[?&]$/, '');
    }


}


// Recognized media file extensions
PdfViewer.MEDIA_IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'avif']);
PdfViewer.MEDIA_VIDEO_EXTS = new Set(['mp4', 'webm', 'ogv', 'mov', 'm4v']);
PdfViewer.MEDIA_AUDIO_EXTS = new Set(['mp3', 'wav', 'ogg', 'oga', 'm4a', 'aac', 'flac']);
// playlist files are handled separately (routed to the Background Music tab as a whole playlist)
PdfViewer.MEDIA_PLAYLIST_EXTS = new Set(['m3u', 'm3u8']);
PdfViewer.MEDIA_EXTENSIONS = new Set([
    ...PdfViewer.MEDIA_IMAGE_EXTS,
    ...PdfViewer.MEDIA_VIDEO_EXTS,
    ...PdfViewer.MEDIA_AUDIO_EXTS,
    ...PdfViewer.MEDIA_PLAYLIST_EXTS,
]);
